import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Settings as SettingsIcon, BookOpen, Plus, Trash2, Save, X, Loader2,
  DollarSign, Shield, Users, Building, Calendar, Clock, FileText,
  Bell, Briefcase, ChevronRight, Globe, Lock, Tag, List, Car,
  ToggleLeft, ToggleRight, Zap, AlertCircle, CheckCircle, RefreshCw,
  Link2, Unlink, ExternalLink, ChevronDown, Eye, EyeOff, KeyRound, ScanLine, QrCode,
  Search, Send
} from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useLocation, Link } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import ContractorBillSettings from './ContractorBillSettings';
import { useConfirm } from '../../components/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';
import clsx from 'clsx';
import CancellationPolicies from './CancellationPolicies';
import ComplianceRequirements from './ComplianceRequirements';
import ServiceTypes from './ServiceTypes';
import ServiceRatios from './ServiceRatios';
import TravelTransport from '../Financials/TravelTransport';
import StaffBroadcast from './StaffBroadcast';
import { SettingsPage, SettingsHeader, Switch } from './SettingsUI';
import TwoFactorSettings from '../../components/security/TwoFactorSettings';

// ─── Toggle ───────────────────────────────────────────────────────────────────
// Every switch on this page is the shared Switch. The old one never moved its knob
// (`translate-x-4.5` is not a Tailwind class), so ON and OFF differed only by colour, and it had
// no switch role and no visible focus.
function Toggle(props) {
  return <Switch {...props} />;
}

// ─── Section metadata: icon + one-line description per section ────────────────
const SECTION_META = {
  'General':                                { icon: Building,    desc: 'Organisation identity, security defaults and regional preferences.' },
  'Service Types':                          { icon: Tag,         desc: 'Define the support and service categories used across shifts and billing.' },
  'Compliance Requirements':                { icon: Shield,      desc: 'Credentials and checks staff must hold to be rostered.' },
  'Cancellation Policies':                  { icon: List,        desc: 'Rules that decide when a cancelled shift is charged or paid.' },
  'Group Ratios':                           { icon: Users,       desc: 'Staff-to-participant ratios for group and shared-support services.' },
  'Travel & Transport':                     { icon: Car,         desc: 'Mileage, travel time and transport charge configuration.' },
  'Scheduler':                              { icon: Calendar,    desc: 'Defaults that govern how shifts are created and rostered.' },
  'Time & Attendance':                      { icon: Clock,       desc: 'Clock-in rules, geofencing, rounding and timesheet precision.' },
  'Declined Shift Flow':                    { icon: AlertCircle, desc: 'What happens when staff decline an assigned shift.' },
  'Document Categories':                    { icon: FileText,    desc: 'Organise client and staff documents into named categories.' },
  'Note Types':                             { icon: List,        desc: 'Configure the progress-note types available to support workers.' },
  'Public Holidays':                        { icon: Globe,       desc: 'State-specific holiday dates used for automatic billing uplift.' },
  'Job Titles':                             { icon: Briefcase,   desc: 'The role titles that can be assigned to staff members.' },
  'Integrations':                           { icon: Zap,         desc: 'Connect Rostery to your accounting software and third-party tools.' },
  'Notifications':                          { icon: Bell,        desc: 'Configure email SMTP and enable automated notification triggers.' },
  'Price Books':                            { icon: BookOpen,    desc: 'NDIS support-item price lists used for client charging.' },
  'Teams':                                  { icon: Users,       desc: 'Group staff into teams for scheduling and reporting.' },
  'Financial Configuration':                { icon: DollarSign,  desc: 'Rates, multipliers, owners, stakeholders and superannuation.' },
  'Security & Two-Factor Authentication':  { icon: KeyRound,    desc: 'Multi-factor authentication and account security controls.' },
};

// ─── Section wrapper ──────────────────────────────────────────────────────────
// A settings card. The save button reflects the section's state: disabled while nothing has
// changed, primary with an "Unsaved changes" note once something has, so a half-edited section is
// visible at a glance (the section nav shows the same dot).
function Section({ title, icon, desc, children, onSave, saving, dirty }) {
  const meta = SECTION_META[title] || {};
  const Icon = icon || meta.icon || SettingsIcon;
  const description = desc ?? meta.desc;
  return (
    <section className="card overflow-hidden">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3 border-b border-slate-100 px-6 py-4">
        <div className="flex min-w-0 items-start gap-3">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-50 text-slate-500 ring-1 ring-inset ring-slate-200/80">
            <Icon size={16} strokeWidth={1.9} />
          </span>
          <div className="min-w-0">
            <h2 className="text-[15px] font-semibold leading-snug tracking-[-0.01em] text-slate-900">{title}</h2>
            {description && <p className="mt-0.5 max-w-xl text-[13px] leading-relaxed text-slate-500 [text-wrap:pretty]">{description}</p>}
          </div>
        </div>
        {onSave && (
          <div className="flex shrink-0 items-center gap-3">
            {dirty && !saving && (
              <span className="flex items-center gap-1.5 text-xs font-medium text-amber-700">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> Unsaved changes
              </span>
            )}
            <button onClick={onSave} disabled={saving || !dirty}
              className={clsx(dirty || saving ? 'btn-primary' : 'btn-secondary', 'btn-sm')}>
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              {saving ? 'Saving…' : dirty ? 'Save changes' : 'Save'}
            </button>
          </div>
        )}
      </div>
      <div className="space-y-6 p-6">
        {children}
      </div>
    </section>
  );
}

// ─── SettingRow: label left, control right ────────────────────────────────────
function SettingRow({ label, hint, children }) {
  return (
    <div className="flex items-center justify-between gap-6 border-b border-slate-100 py-3 last:border-0">
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-800">{label}</p>
        {hint && <p className="mt-0.5 max-w-xl text-xs leading-relaxed text-slate-500">{hint}</p>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

// A bordered run of SettingRows; the row dividers sit inside the padding.
function SettingGroup({ children }) {
  return <div className="rounded-xl border border-slate-200/80 px-4">{children}</div>;
}

// Small heading for a group of fields inside a section.
function GroupHeading({ children, hint }) {
  return (
    <div className="mb-3">
      <h3 className="text-sm font-semibold text-slate-900">{children}</h3>
      {hint && <p className="mt-0.5 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

// ─── TagList: pill tags with X delete ────────────────────────────────────────
function TagList({ items, onDelete, labelKey = 'name' }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map(item => (
        <span key={item.id}
          className="inline-flex items-center gap-1.5 rounded-lg bg-slate-50 py-1 pl-3 pr-1.5 text-sm font-medium text-slate-700 ring-1 ring-inset ring-slate-200/80">
          {item[labelKey]}
          {onDelete && (
            <button onClick={() => onDelete(item.id)} aria-label={`Remove ${item[labelKey]}`}
              className="rounded p-0.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600">
              <X size={12} />
            </button>
          )}
        </span>
      ))}
    </div>
  );
}

// ─── AddTagInput: input + Add button ─────────────────────────────────────────
function AddTagInput({ value, onChange, onAdd, placeholder = 'Add new…', disabled }) {
  const handleKey = (e) => { if (e.key === 'Enter') { e.preventDefault(); onAdd(); } };
  return (
    <div className="flex gap-2">
      <input
        className="input-field flex-1 !py-2 text-sm"
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={handleKey}
        disabled={disabled}
      />
      <button onClick={onAdd} disabled={disabled || !value.trim()} className="btn-primary btn-sm px-3 gap-1">
        <Plus size={13} /> Add
      </button>
    </div>
  );
}

// ─── Price Books (preserved from original) ───────────────────────────────────
function PriceBooks() {
  const [books, setBooks]       = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [newBook, setNewBook]   = useState('');
  const [itemForm, setItemForm] = useState({ supportItemNumber: '', supportItemName: '', rate: '', gst: false, unit: 'hourly' });

  const load = () => {
    setLoading(true);
    api.get('/price-books').then(r => setBooks(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const loadDetail = (id) => {
    setSelected(id);
    api.get(`/price-books/${id}`).then(r => setDetail(r.data));
  };

  const createBook = async () => {
    if (!newBook.trim()) return;
    await api.post('/price-books', { name: newBook });
    setNewBook(''); load(); toast.success('Price book created');
  };

  const addItem = async () => {
    if (!itemForm.supportItemName || !itemForm.rate) return toast.error('Name and rate are required');
    setSaving(true);
    try {
      await api.post(`/price-books/${selected}/items`, itemForm);
      setItemForm({ supportItemNumber: '', supportItemName: '', rate: '', gst: false, unit: 'hourly' });
      loadDetail(selected); toast.success('Item added');
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const deleteItem = async (itemId) => {
    await api.delete(`/price-books/items/${itemId}`);
    loadDetail(selected); toast.success('Item removed');
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div className="card p-4 space-y-3">
        <p className="font-semibold text-slate-700 text-sm">Price Books</p>
        <div className="flex gap-2">
          <input className="input-field flex-1 !py-2 text-sm" placeholder="New book name…"
            value={newBook} onChange={e => setNewBook(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && createBook()} />
          <button onClick={createBook} className="btn-primary btn-sm"><Plus size={13} /></button>
        </div>
        {loading
          ? <div className="flex justify-center py-4"><div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" /></div>
          : (
            <div className="space-y-1">
              {books.map(b => (
                <button key={b.id} onClick={() => loadDetail(b.id)}
                  className={clsx('w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm transition-colors',
                    selected === b.id ? 'bg-primary-50 text-primary-700 font-medium' : 'hover:bg-slate-50 text-slate-700')}>
                  <span>{b.name}</span>
                  <span className="text-xs text-slate-400">{b.item_count} items</span>
                </button>
              ))}
            </div>
          )
        }
      </div>

      <div className="lg:col-span-2 card p-4">
        {!detail ? (
          <div className="flex items-center justify-center h-40 text-slate-400 text-sm">
            <BookOpen size={32} className="text-slate-200 mr-3" /> Select a price book
          </div>
        ) : (
          <div className="space-y-4">
            <p className="font-semibold text-slate-900">{detail.name}</p>
            <div className="grid grid-cols-4 gap-2">
              <input className="input-field !py-2 text-sm" placeholder="Item no."
                value={itemForm.supportItemNumber} onChange={e => setItemForm(f => ({ ...f, supportItemNumber: e.target.value }))} />
              <input className="input-field !py-2 text-sm col-span-2" placeholder="Support item name *"
                value={itemForm.supportItemName} onChange={e => setItemForm(f => ({ ...f, supportItemName: e.target.value }))} />
              <div className="flex gap-1">
                <input type="number" step="0.01" className="input-field !py-2 text-sm flex-1" placeholder="Rate *"
                  value={itemForm.rate} onChange={e => setItemForm(f => ({ ...f, rate: e.target.value }))} />
                <button onClick={addItem} disabled={saving} className="btn-primary btn-sm px-3">
                  {saving ? <Loader2 size={12} className="animate-spin" /> : <Plus size={13} />}
                </button>
              </div>
            </div>
            <div className="table-wrapper rounded-xl overflow-x-auto">
              <table className="table-base min-w-[500px]">
                <thead><tr><th>Item No.</th><th>Name</th><th>Rate</th><th>Unit</th><th></th></tr></thead>
                <tbody>
                  {(detail.items || []).map(item => (
                    <tr key={item.id}>
                      <td className="text-xs font-mono text-slate-500">{item.support_item_number || '—'}</td>
                      <td className="text-sm">{item.support_item_name}</td>
                      <td className="font-semibold text-emerald-600">${parseFloat(item.rate).toFixed(2)}</td>
                      <td className="text-xs text-slate-400">{item.unit}</td>
                      <td><button onClick={() => deleteItem(item.id)} className="p-1 text-red-400 hover:text-red-600"><Trash2 size={12} /></button></td>
                    </tr>
                  ))}
                  {(detail.items || []).length === 0 && <tr><td colSpan={5} className="text-center py-6 text-slate-400">No items yet — add above</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}



// ─── Teams (preserved from original) ─────────────────────────────────────────
function Teams() {
  const [teams, setTeams]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(null);
  const [saving, setSaving]   = useState(false);
  const [form, setForm]       = useState({ name: '', description: '', color: '#6366f1' });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const load = () => {
    setLoading(true);
    api.get('/teams').then(r => setTeams(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openModal = (t) => {
    setForm(t ? { name: t.name, description: t.description || '', color: t.color || '#6366f1' } : { name: '', description: '', color: '#6366f1' });
    setModal(t || {});
  };

  const save = async () => {
    if (!form.name) return toast.error('Name required');
    setSaving(true);
    try {
      if (modal?.id) { await api.put(`/teams/${modal.id}`, form); toast.success('Team updated'); }
      else { await api.post('/teams', form); toast.success('Team created'); }
      setModal(null); load();
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={() => openModal(null)} className="btn-primary btn-sm"><Plus size={13} /> New Team</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading
          ? <div className="col-span-3 flex justify-center py-8"><div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" /></div>
          : teams.map(t => (
            <div key={t.id} className="card p-4 flex items-center gap-3 cursor-pointer hover:shadow-card-hover" onClick={() => openModal(t)}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: (t.color || '#6366f1') + '20' }}>
                <Users size={18} style={{ color: t.color || '#6366f1' }} />
              </div>
              <div>
                <p className="font-semibold text-slate-900">{t.name}</p>
                {t.description && <p className="text-xs text-slate-400 truncate">{t.description}</p>}
              </div>
            </div>
          ))
        }
      </div>

      <AnimatePresence>
        {modal !== null && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setModal(null)} />
            <motion.div initial={{ scale: .95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: .95, opacity: 0 }}
              className="relative bg-white rounded-2xl shadow-modal w-full max-w-sm z-10">
              <div className="flex items-center justify-between p-5 border-b border-slate-100">
                <h2 className="text-lg font-bold">{modal?.id ? 'Edit Team' : 'New Team'}</h2>
                <button onClick={() => setModal(null)} className="p-2 rounded-xl hover:bg-slate-100 text-slate-400"><X size={16} /></button>
              </div>
              <div className="p-5 space-y-4">
                <div><label className="input-label">Team Name *</label><input className="input-field" value={form.name} onChange={e => set('name', e.target.value)} /></div>
                <div><label className="input-label">Description</label><textarea className="input-field min-h-[60px] resize-y" value={form.description} onChange={e => set('description', e.target.value)} /></div>
                <div>
                  <label className="input-label">Color</label>
                  <div className="flex items-center gap-3 mt-1">
                    <input type="color" value={form.color} onChange={e => set('color', e.target.value)} className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer" />
                    <span className="text-sm text-slate-500">{form.color}</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-3 justify-end p-5 border-t border-slate-100">
                <button onClick={() => setModal(null)} className="btn-secondary">Cancel</button>
                <button onClick={save} disabled={saving} className="btn-primary">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── TIMEZONES & COUNTRIES ────────────────────────────────────────────────────
const TIMEZONES = [
  'Australia/Sydney', 'Australia/Melbourne', 'Australia/Brisbane',
  'Australia/Adelaide', 'Australia/Perth', 'Australia/Darwin',
  'Australia/Hobart', 'Pacific/Auckland', 'Asia/Singapore',
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'Europe/London', 'Europe/Paris', 'UTC',
];

const COUNTRIES = [
  'Australia', 'New Zealand', 'United Kingdom', 'United States', 'Canada',
  'Singapore', 'Ireland', 'South Africa',
];

// ─── NDIA Direct API Integration ─────────────────────────────────────────────
function NDIAIntegration() {
  const [status, setStatus]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [config, setConfig] = useState({ clientId: '', clientSecret: '', providerNumber: '', environment: 'sandbox' });

  const load = () => {
    api.get('/integrations/ndia/status').then(r => setStatus(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const saveConfig = async () => {
    try {
      await api.post('/integrations/ndia/configure', {
        client_id: config.clientId, client_secret: config.clientSecret,
        provider_number: config.providerNumber, environment: config.environment,
      });
      toast.success('NDIA credentials saved');
      setConfigOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to save'); }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const { data } = await api.post('/integrations/ndia/test');
      toast.success(data.message);
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Connection failed'); }
    finally { setTesting(false); }
  };

  const submitClaims = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post('/integrations/ndia/submit-claims', {});
      toast.success(data.message);
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Submission failed'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="flex items-center gap-2 py-4 text-slate-400"><Loader2 size={16} className="animate-spin"/> Loading NDIA status…</div>;

  return (
    <div className="border border-slate-200 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center text-white font-bold text-xs">NDIA</div>
          <div>
            <p className="font-semibold text-slate-800 text-sm">NDIA Direct API</p>
            <p className="text-xs text-slate-400">Submit claims directly — no manual PRODA CSV upload</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {status?.connected
            ? <span className="inline-flex items-center gap-1 text-xs bg-emerald-100 text-emerald-700 px-2.5 py-1 rounded-full font-semibold"><CheckCircle size={11}/> Configured</span>
            : <span className="text-xs text-slate-400 bg-slate-100 px-2.5 py-1 rounded-full">Not configured</span>
          }
          <button onClick={() => setConfigOpen(c => !c)} className="text-xs btn-secondary px-3 py-1.5">
            {configOpen ? 'Close' : 'Configure'}
          </button>
        </div>
      </div>

      {status?.connected && (
        <div className="text-xs text-slate-500 space-y-0.5 pl-12">
          <p>Environment: <span className={`font-semibold ${status.environment === 'production' ? 'text-emerald-600' : 'text-amber-600'}`}>{status.environment}</span></p>
          {status.providerNumber && <p>Provider No: <span className="font-semibold text-slate-700">{status.providerNumber}</span></p>}
          {status.lastSubmit && <p>Last submit: <span className="font-semibold text-slate-700">{new Date(status.lastSubmit).toLocaleString('en-AU')} — {status.lastSubmitCount} claim(s) — <span className={status.lastSubmitStatus === 'success' ? 'text-emerald-600' : 'text-red-500'}>{status.lastSubmitStatus}</span></span></p>}
        </div>
      )}

      {configOpen && (
        <div className="bg-slate-50 rounded-xl p-4 space-y-3 border border-slate-100">
          <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">NDIA B2B Credentials</p>
          <p className="text-xs text-slate-500">Register for B2B Connector access at <a href="https://www.ndis.gov.au/providers/working-provider/myplace-provider-portal" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">NDIS Provider Portal</a>. Use sandbox for testing before going live.</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="input-label">Environment</label>
              <select className="input-field" value={config.environment} onChange={e => setConfig(c => ({ ...c, environment: e.target.value }))}>
                <option value="sandbox">Sandbox (testing)</option>
                <option value="production">Production (live claims)</option>
              </select>
            </div>
            <div>
              <label className="input-label">Provider Number</label>
              <input className="input-field" placeholder="Your NDIS Provider Number" value={config.providerNumber} onChange={e => setConfig(c => ({ ...c, providerNumber: e.target.value }))} />
            </div>
            <div>
              <label className="input-label">B2B Client ID</label>
              <input className="input-field" placeholder="Client ID from PRODA B2B" value={config.clientId} onChange={e => setConfig(c => ({ ...c, clientId: e.target.value }))} />
            </div>
            <div>
              <label className="input-label">B2B Client Secret</label>
              <input className="input-field" type="password" placeholder="Client Secret" value={config.clientSecret} onChange={e => setConfig(c => ({ ...c, clientSecret: e.target.value }))} />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={saveConfig} className="btn-primary text-xs px-4">Save Credentials</button>
            <button onClick={testConnection} disabled={testing} className="btn-secondary text-xs px-4 flex items-center gap-1.5">
              {testing ? <Loader2 size={11} className="animate-spin"/> : null} Test Connection
            </button>
          </div>
        </div>
      )}

      {status?.connected && (
        <div className="flex gap-2 pl-12">
          <button onClick={submitClaims} disabled={submitting} className="btn-primary text-xs px-4 flex items-center gap-1.5">
            {submitting ? <Loader2 size={11} className="animate-spin"/> : null}
            Submit All Pending Claims
          </button>
          <p className="text-xs text-slate-400 self-center">Submits approved NDIA invoices with support item numbers</p>
        </div>
      )}
    </div>
  );
}

// ─── Xero Integration Component ──────────────────────────────────────────────
function XeroIntegration() {
  const confirm = useConfirm();
  const [status, setStatus]         = useState(null);
  const [loading, setLoading]       = useState(true);
  const [configOpen, setConfigOpen] = useState(false);
  const [connecting, setConnecting]           = useState(false);
  const [mockConnecting, setMockConnecting]   = useState(false);
  const [disconnecting, setDisconnecting]     = useState(false);
  const [showSecret, setShowSecret]           = useState(false);
  const [config, setConfig] = useState({ clientId: '', clientSecret: '', accountCode: '200', taxType: 'EXEMPTOUTPUT' });

  const load = () => {
    setLoading(true);
    api.get('/integrations/xero/status')
      .then(r => { setStatus(r.data); if (r.data.hasCredentials) setConfigOpen(false); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const saveConfig = async () => {
    if (!config.clientId || !config.clientSecret) return toast.error('Client ID and secret are required');
    try {
      await api.post('/integrations/xero/configure', config);
      toast.success('Xero credentials saved');
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Save failed'); }
  };

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get('/integrations/xero/auth-url');
      window.location.href = data.authUrl;
    } catch (e) { toast.error(e.response?.data?.message || 'Connect failed'); setConnecting(false); }
  };

  const mockConnect = async () => {
    setMockConnecting(true);
    try {
      await api.post('/integrations/xero/mock-connect');
      toast.success('Test connection established — using demo data');
      load();
    } catch (e) { toast.error('Mock connect failed'); }
    finally { setMockConnecting(false); }
  };

  const disconnect = async () => {
    if (!await confirm('Disconnect Xero? Your credentials will be preserved.', { title: 'Disconnect Xero', danger: true })) return;
    setDisconnecting(true);
    try {
      await api.post('/integrations/xero/disconnect');
      toast.success('Disconnected from Xero');
      load();
    } catch (e) { toast.error('Disconnect failed'); }
    finally { setDisconnecting(false); }
  };

  const release = async () => {
    if (!await confirm(`Another Rostery account will then be able to connect ${status?.ownedTenantName}. Only do this if this company's books are moving to a different Rostery account.`,
      { title: `Release ${status?.ownedTenantName}?`, danger: true, confirmLabel: 'Release' })) return;
    try { const { data } = await api.post('/integrations/xero/release'); toast.success(data.message); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Release failed'); }
  };

  if (loading) return <div className="flex items-center gap-2 py-4 text-slate-400"><Loader2 size={16} className="animate-spin"/> Loading Xero status…</div>;

  const connected = status?.connected;

  return (
    <div className="space-y-4">
      {/* Status Card */}
      <div className={clsx('flex items-center justify-between p-4 rounded-xl border', connected ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200')}>
        <div className="flex items-center gap-3">
          <div className={clsx('w-10 h-10 rounded-xl flex items-center justify-center font-bold text-white text-sm', connected ? 'bg-emerald-500' : 'bg-slate-400')}>
            X
          </div>
          <div>
            <p className="font-semibold text-slate-800 text-sm">Xero</p>
            <p className="text-xs text-slate-500">
              {connected ? `Connected to ${status.tenantName}` : (status?.ownedTenantName ? `Not connected · belongs to ${status.ownedTenantName}` : 'Not connected')}
              {status?.lastSync && ` · Invoices: ${new Date(status.lastSync).toLocaleDateString('en-AU')} (${status.lastSyncCount})`}
              {status?.lastPayrollSync && ` · Payroll: ${new Date(status.lastPayrollSync).toLocaleDateString('en-AU')} (${status.lastPayrollSyncCount})`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {connected ? (
            <>
              {/* Sending lives on the screens where the work is: invoices and timesheets. */}
              <Link to="/invoices?tab=xero"
                className="flex items-center gap-1.5 px-3 py-1.5 border border-emerald-300 bg-white hover:bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium">
                <RefreshCw size={12}/> Send invoices
              </Link>
              <Link to="/timesheets?tab=xero"
                className="flex items-center gap-1.5 px-3 py-1.5 border border-violet-300 bg-white hover:bg-violet-50 text-violet-700 rounded-lg text-xs font-medium">
                <RefreshCw size={12}/> Send timesheets
              </Link>
              {/* Reconnect keeps today's connection until Xero says yes, so a refused consent never
                  leaves the org disconnected. Needed once to grant the account and payroll permissions. */}
              <button onClick={connect} disabled={connecting} title="Grant Rostery any new Xero permissions"
                className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 rounded-lg text-xs font-medium">
                {connecting ? <Loader2 size={12} className="animate-spin"/> : <Link2 size={12}/>} Reconnect
              </button>
              <button onClick={disconnect} disabled={disconnecting}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 bg-white hover:bg-red-50 text-slate-600 hover:text-red-600 rounded-lg text-xs font-medium">
                <Unlink size={12}/> Disconnect
              </button>
            </>
          ) : (
            <>
              {status?.ownedTenantName && (
                <button onClick={release} title="Let another Rostery account connect this Xero organisation"
                  className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 bg-white hover:bg-red-50 text-slate-600 hover:text-red-600 rounded-lg text-xs font-medium">
                  <Unlink size={12}/> Release
                </button>
              )}
              <button onClick={() => setConfigOpen(s => !s)}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 bg-white hover:bg-slate-50 text-slate-600 rounded-lg text-xs font-medium">
                <SettingsIcon size={12}/> Configure
              </button>
              <button onClick={connect} disabled={!status?.hasCredentials || connecting}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#13B5EA] hover:bg-[#0ea5d4] text-white rounded-lg text-xs font-semibold disabled:opacity-50">
                {connecting ? <Loader2 size={12} className="animate-spin"/> : <Link2 size={12}/>} Connect Xero
              </button>
            </>
          )}
        </div>
      </div>

      {/* Config Panel */}
      {configOpen && (
        <div className="border border-slate-200 rounded-xl p-4 space-y-3 bg-white">
          <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Xero OAuth App Credentials</p>
          <p className="text-xs text-slate-500">Create an OAuth 2.0 app at <a href="https://developer.xero.com/app/manage" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">developer.xero.com <ExternalLink size={10} className="inline"/></a> and paste the credentials below.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="input-label">Client ID</label>
              <input className="input-field" placeholder="Your Xero Client ID" value={config.clientId} onChange={e => setConfig(c => ({ ...c, clientId: e.target.value }))}/>
            </div>
            <div>
              <label className="input-label">Client Secret</label>
              <div className="relative">
                <input className="input-field pr-8" type={showSecret ? 'text' : 'password'} placeholder="Your Xero Client Secret" value={config.clientSecret} onChange={e => setConfig(c => ({ ...c, clientSecret: e.target.value }))}/>
                <button type="button" onClick={() => setShowSecret(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400"><EyeOff size={14}/></button>
              </div>
            </div>
            <div>
              <label className="input-label">Sales Account Code</label>
              <input className="input-field" placeholder="200" value={config.accountCode} onChange={e => setConfig(c => ({ ...c, accountCode: e.target.value }))}/>
            </div>
            <div>
              <label className="input-label">Tax Type</label>
              <select className="input-field" value={config.taxType} onChange={e => setConfig(c => ({ ...c, taxType: e.target.value }))}>
                <option value="EXEMPTOUTPUT">EXEMPTOUTPUT (GST-free)</option>
                <option value="OUTPUT">OUTPUT (GST on income)</option>
                <option value="NONE">NONE</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={() => setConfigOpen(false)} className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
            <button onClick={saveConfig} className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium">Save Credentials</button>
          </div>
        </div>
      )}

    </div>
  );
}

// ─── QuickBooks Integration Component ────────────────────────────────────────
function QuickBooksIntegration() {
  const confirm = useConfirm();
  const [status, setStatus]         = useState(null);
  const [loading, setLoading]       = useState(true);
  const [configOpen, setConfigOpen] = useState(false);
  const [syncOpen, setSyncOpen]     = useState(false);
  const [syncing, setSyncing]       = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [mockConnecting, setMockConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [config, setConfig] = useState({ clientId: '', clientSecret: '', environment: 'sandbox', incomeAccountRef: '1' });
  const [syncRange, setSyncRange]             = useState({ startDate: '', endDate: '' });
  const [syncingPayroll, setSyncingPayroll]   = useState(false);
  const [syncPayrollOpen, setSyncPayrollOpen] = useState(false);
  const [payrollRange, setPayrollRange]       = useState({ startDate: '', endDate: '' });

  const load = () => {
    setLoading(true);
    api.get('/integrations/quickbooks/status')
      .then(r => setStatus(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const saveConfig = async () => {
    if (!config.clientId || !config.clientSecret) return toast.error('Client ID and secret are required');
    try {
      await api.post('/integrations/quickbooks/configure', config);
      toast.success('QuickBooks credentials saved');
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Save failed'); }
  };

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get('/integrations/quickbooks/auth-url');
      window.location.href = data.authUrl;
    } catch (e) { toast.error(e.response?.data?.message || 'Connect failed'); setConnecting(false); }
  };

  const mockConnect = async () => {
    setMockConnecting(true);
    try {
      await api.post('/integrations/quickbooks/mock-connect');
      toast.success('Test connection established — using demo data');
      load();
    } catch (e) { toast.error('Mock connect failed'); }
    finally { setMockConnecting(false); }
  };

  const disconnect = async () => {
    if (!await confirm('Disconnect QuickBooks?', { title: 'Disconnect QuickBooks', danger: true })) return;
    setDisconnecting(true);
    try {
      await api.post('/integrations/quickbooks/disconnect');
      toast.success('Disconnected from QuickBooks');
      load();
    } catch { toast.error('Disconnect failed'); }
    finally { setDisconnecting(false); }
  };

  const syncInvoices = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post('/integrations/quickbooks/sync', syncRange);
      toast.success(data.message);
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Sync failed'); }
    finally { setSyncing(false); }
  };

  const syncPayroll = async () => {
    setSyncingPayroll(true);
    try {
      const { data } = await api.post('/integrations/quickbooks/sync-payroll', payrollRange);
      toast.success(data.message);
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Payroll sync failed'); }
    finally { setSyncingPayroll(false); }
  };

  if (loading) return <div className="flex items-center gap-2 py-4 text-slate-400"><Loader2 size={16} className="animate-spin"/> Loading QuickBooks status…</div>;

  const connected = status?.connected;

  return (
    <div className="space-y-4">
      <div className={clsx('flex items-center justify-between p-4 rounded-xl border', connected ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200')}>
        <div className="flex items-center gap-3">
          <div className={clsx('w-10 h-10 rounded-xl flex items-center justify-center font-bold text-white text-xs', connected ? 'bg-[#2CA01C]' : 'bg-slate-400')}>
            QB
          </div>
          <div>
            <p className="font-semibold text-slate-800 text-sm">QuickBooks Online</p>
            <p className="text-xs text-slate-500">
              {connected ? `Connected to ${status.companyName}` : `Not connected · ${status?.environment || 'sandbox'} mode`}
              {status?.lastSync && ` · Invoices: ${new Date(status.lastSync).toLocaleDateString('en-AU')} (${status.lastSyncCount})`}
              {status?.lastPayrollSync && ` · Payroll: ${new Date(status.lastPayrollSync).toLocaleDateString('en-AU')} (${status.lastPayrollSyncCount})`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {connected ? (
            <>
              {/* Sending happens in the QuickBooks tabs (preview, never twice); the old sync buttons
                  resent the latest invoices on every press. */}
              <Link to="/invoices?tab=quickbooks"
                className="flex items-center gap-1.5 px-3 py-1.5 border border-emerald-300 bg-white hover:bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium">
                <ExternalLink size={12}/> Invoices tab
              </Link>
              <Link to="/timesheets?tab=quickbooks"
                className="flex items-center gap-1.5 px-3 py-1.5 border border-violet-300 bg-white hover:bg-violet-50 text-violet-700 rounded-lg text-xs font-medium">
                <ExternalLink size={12}/> Timesheets tab
              </Link>
              <button onClick={disconnect} disabled={disconnecting}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 bg-white hover:bg-red-50 text-slate-600 hover:text-red-600 rounded-lg text-xs font-medium">
                <Unlink size={12}/> Disconnect
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setConfigOpen(s => !s)}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 bg-white hover:bg-slate-50 text-slate-600 rounded-lg text-xs font-medium">
                <SettingsIcon size={12}/> Configure
              </button>
              <button onClick={connect} disabled={!status?.hasCredentials || connecting}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#2CA01C] hover:bg-[#258a17] text-white rounded-lg text-xs font-semibold disabled:opacity-50">
                {connecting ? <Loader2 size={12} className="animate-spin"/> : <Link2 size={12}/>} Connect QuickBooks
              </button>
            </>
          )}
        </div>
      </div>

      {configOpen && (
        <div className="border border-slate-200 rounded-xl p-4 space-y-3 bg-white">
          <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">QuickBooks OAuth App Credentials</p>
          <p className="text-xs text-slate-500">Create an app at <a href="https://developer.intuit.com" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">developer.intuit.com <ExternalLink size={10} className="inline"/></a>. Use sandbox for testing.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="input-label">Client ID</label>
              <input className="input-field" placeholder="Your QB Client ID" value={config.clientId} onChange={e => setConfig(c => ({ ...c, clientId: e.target.value }))}/>
            </div>
            <div>
              <label className="input-label">Client Secret</label>
              <div className="relative">
                <input className="input-field pr-8" type={showSecret ? 'text' : 'password'} placeholder="Your QB Client Secret" value={config.clientSecret} onChange={e => setConfig(c => ({ ...c, clientSecret: e.target.value }))}/>
                <button type="button" onClick={() => setShowSecret(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400"><EyeOff size={14}/></button>
              </div>
            </div>
            <div>
              <label className="input-label">Environment</label>
              <select className="input-field" value={config.environment} onChange={e => setConfig(c => ({ ...c, environment: e.target.value }))}>
                <option value="sandbox">Sandbox (testing)</option>
                <option value="production">Production</option>
              </select>
            </div>
            <div>
              <label className="input-label">Income Account Ref</label>
              <input className="input-field" placeholder="1" value={config.incomeAccountRef} onChange={e => setConfig(c => ({ ...c, incomeAccountRef: e.target.value }))}/>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={() => setConfigOpen(false)} className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm text-slate-600">Cancel</button>
            <button onClick={saveConfig} className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium">Save Credentials</button>
          </div>
        </div>
      )}

      {connected && syncOpen && (
        <div className="border border-slate-200 rounded-xl p-4 space-y-3 bg-white">
          <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Sync Invoices to QuickBooks</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="input-label">From Date</label>
              <input type="date" className="input-field" value={syncRange.startDate} onChange={e => setSyncRange(r => ({ ...r, startDate: e.target.value }))}/>
            </div>
            <div>
              <label className="input-label">To Date</label>
              <input type="date" className="input-field" value={syncRange.endDate} onChange={e => setSyncRange(r => ({ ...r, endDate: e.target.value }))}/>
            </div>
          </div>
          <p className="text-xs text-slate-400">Leave dates blank to sync all non-draft invoices (up to 50).</p>
          <div className="flex justify-end gap-2">
            <button onClick={() => setSyncOpen(false)} className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm text-slate-600">Cancel</button>
            <button onClick={syncInvoices} disabled={syncing}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#2CA01C] hover:bg-[#258a17] text-white rounded-lg text-sm font-medium disabled:opacity-60">
              {syncing ? <Loader2 size={13} className="animate-spin"/> : <RefreshCw size={13}/>}
              {syncing ? 'Syncing…' : 'Sync Now'}
            </button>
          </div>
        </div>
      )}

      {/* QB Payroll Sync Panel */}
      {connected && syncPayrollOpen && (
        <div className="border border-violet-200 rounded-xl p-4 space-y-3 bg-white">
          <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Sync Payroll to QuickBooks</p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
            Approved timesheets are pushed as Time Activities in QuickBooks. Staff must have their QuickBooks Employee ID set in their profile (External System ID field).
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="input-label">From Date</label>
              <input type="date" className="input-field" value={payrollRange.startDate} onChange={e => setPayrollRange(r => ({ ...r, startDate: e.target.value }))}/>
            </div>
            <div>
              <label className="input-label">To Date</label>
              <input type="date" className="input-field" value={payrollRange.endDate} onChange={e => setPayrollRange(r => ({ ...r, endDate: e.target.value }))}/>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setSyncPayrollOpen(false)} className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm text-slate-600">Cancel</button>
            <button onClick={syncPayroll} disabled={syncingPayroll}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-sm font-medium disabled:opacity-60">
              {syncingPayroll ? <Loader2 size={13} className="animate-spin"/> : <RefreshCw size={13}/>}
              {syncingPayroll ? 'Syncing…' : 'Sync Payroll Now'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const OTHER_INTEGRATIONS = [
  {
    key: 'csv_import',
    slug: 'csv-import',
    label: 'CSV Import',
    description: 'Import clients and staff from CSV files.',
    color: '#6366f1',
    abbr: 'CSV',
    isCsv: true,
    syncLabel: 'Import CSV',
    setupNote: 'No credentials needed. Just choose your CSV format settings below, then use the Import CSV button to upload a file.',
    fields: [
      { key: 'date_format', label: 'Date Format', type: 'select', options: ['DD/MM/YYYY','MM/DD/YYYY','YYYY-MM-DD'], default: 'DD/MM/YYYY' },
      { key: 'delimiter',   label: 'Delimiter',   type: 'select', options: ['Comma (,)','Semicolon (;)','Tab'], default: 'Comma (,)' },
    ],
  },
  {
    key: 'employment_hero',
    slug: 'employment-hero',
    label: 'Employment Hero Payroll',
    description: 'Send approved timesheets to Employment Hero for payroll.',
    color: '#e11d48',
    abbr: 'EH',
    isOAuth: true,
    // Sending timesheets and adding staff happen in Timesheets › Employment Hero payroll.
    manageIn: { label: 'Open payroll tab', to: '/timesheets?tab=employment-hero' },
    setupNote: `In Employment Hero open Developer Portal › API › Add New Application. Set the redirect URI to ${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/integrations/employment-hero/callback, then paste the client ID and secret here, save, and press Connect. Staff are matched to Employment Hero employees by email. Then send timesheets and add staff from Timesheets › Employment Hero payroll.`,
    setupLink: { label: 'Employment Hero API docs', href: 'https://developer.employmenthero.com/api-references' },
    fields: [
      { key: 'client_id',     label: 'Client ID',     type: 'text',     placeholder: 'From the Developer Portal' },
      { key: 'client_secret', label: 'Client Secret', type: 'password', placeholder: 'Leave blank to keep the saved secret' },
    ],
  },
  {
    key: 'google_calendar',
    slug: 'google-calendar',
    label: 'Google Calendar',
    description: 'Sync upcoming shifts to Google Calendar.',
    color: '#4285F4',
    abbr: 'GC',
    isOAuth: true,
    syncLabel: 'Sync Shifts',
    setupNote: `Create an OAuth 2.0 Web App at Google Cloud Console. Enable the Calendar API, then add redirect URI: ${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/integrations/google-calendar/callback. Paste the Client ID and Secret below, then click Connect.`,
    setupLink: { label: 'Google Cloud Console', href: 'https://console.cloud.google.com/apis/credentials' },
    fields: [
      { key: 'client_id',     label: 'OAuth Client ID',     type: 'text',     placeholder: 'xxxx.apps.googleusercontent.com' },
      { key: 'client_secret', label: 'OAuth Client Secret', type: 'password', placeholder: 'GOCSPX-...' },
      { key: 'calendar_id',   label: 'Calendar ID',         type: 'text',     placeholder: 'primary (or specific calendar ID)' },
    ],
  },
  {
    key: 'hubspot',
    slug: 'hubspot',
    label: 'HubSpot',
    description: 'Sync clients as HubSpot CRM contacts.',
    color: '#FF7A59',
    abbr: 'HS',
    syncLabel: 'Sync Clients',
    setupNote: 'Create a Private App in HubSpot: Settings → Integrations → Private Apps → Create. Enable scopes: crm.objects.contacts.write. Copy the access token (starts with pat-na1-...).',
    setupLink: { label: 'HubSpot Private Apps', href: 'https://app.hubspot.com/private-apps' },
    fields: [
      { key: 'api_key',   label: 'Private App Token', type: 'password', placeholder: 'pat-na1-xxxxxxxx-...' },
      { key: 'portal_id', label: 'Portal ID',         type: 'text',     placeholder: 'e.g. 12345678 (in HubSpot URL)' },
    ],
  },
  {
    key: 'pay_cat',
    slug: 'pay-cat',
    label: 'Pay Cat',
    description: 'Export approved timesheets to Pay Cat payroll.',
    color: '#0ea5e9',
    abbr: 'PC',
    syncLabel: 'Export Timesheets',
    setupNote: 'Log in to Pay Cat, go to Settings → API Access to generate your API key. Your Company Code is shown on your Pay Cat dashboard or can be obtained from Pay Cat support.',
    setupLink: { label: 'Pay Cat login', href: 'https://app.paycat.com.au' },
    fields: [
      { key: 'api_key',      label: 'API Key',      type: 'password', placeholder: 'Your Pay Cat API key' },
      { key: 'company_code', label: 'Company Code', type: 'text',     placeholder: 'e.g. MYCOMPANY01' },
    ],
  },
  {
    key: 'intellihr',
    slug: 'intellihr',
    label: 'intelliHR',
    description: 'Sync staff profiles and records to intelliHR.',
    color: '#7c3aed',
    abbr: 'IQ',
    syncLabel: 'Sync Staff',
    setupNote: 'In intelliHR go to Settings → Integrations → API Keys and generate a new key. Your subdomain is the part before .intellihr.io in your intelliHR URL (e.g. yourcompany.intellihr.io).',
    setupLink: { label: 'intelliHR API docs', href: 'https://developers.intellihr.io' },
    fields: [
      { key: 'api_key',   label: 'API Key',   type: 'password', placeholder: 'Your intelliHR API key' },
      { key: 'subdomain', label: 'Subdomain', type: 'text',     placeholder: 'yourcompany (from yourcompany.intellihr.io)' },
    ],
  },
  {
    key: 'careplan_connect',
    slug: 'careplan',
    label: 'CarePlan Connect',
    description: 'Push active care plans to CarePlan Connect.',
    color: '#059669',
    abbr: 'CP',
    syncLabel: 'Sync Care Plans',
    setupNote: 'Obtain your API endpoint and token from your CarePlan Connect account manager or from Settings → API in your CarePlan Connect portal.',
    fields: [
      { key: 'api_endpoint', label: 'API Endpoint', type: 'text',     placeholder: 'https://api.careplanconnect.com.au' },
      { key: 'api_token',    label: 'API Token',    type: 'password', placeholder: 'Your API token' },
    ],
  },
  {
    key: 'aura',
    slug: 'aura',
    label: 'Aura',
    description: 'Submit NDIS claims and invoices to Aura.',
    color: '#d97706',
    abbr: 'AU',
    syncLabel: 'Sync NDIS Claims',
    setupNote: 'Contact Aura support to obtain your API key and Tenant ID. These are available in your Aura account under Settings → API Credentials.',
    fields: [
      { key: 'api_key',   label: 'API Key',   type: 'password', placeholder: 'Your Aura API key' },
      { key: 'tenant_id', label: 'Tenant ID', type: 'text',     placeholder: 'e.g. org-xxxxxxxx' },
    ],
  },
];

// ─── OtherIntegrationCard ─────────────────────────────────────────────────────
function OtherIntegrationCard({ integration }) {
  const confirm = useConfirm();
  const [status, setStatus]       = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [csvOpen, setCsvOpen]     = useState(false);
  const [syncing, setSyncing]     = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [mockConnecting, setMockConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});
  const [fields, setFields]       = useState({});
  const [csvFile, setCsvFile]     = useState(null);
  const [csvType, setCsvType]     = useState('clients');
  const [csvResult, setCsvResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const slug = integration.slug;

  const load = () => {
    api.get(`/integrations/${slug}/status`).then(r => {
      setStatus(r.data);
      if (r.data?.config) {
        setFields(prev => {
          const prefill = {};
          Object.entries(r.data.config).forEach(([k, v]) => {
            if (v && !prev[k]) prefill[k] = v;
          });
          return { ...prefill, ...prev };
        });
      }
    }).catch(() => setStatus({ connected: false }));
  };

  useEffect(() => { load(); }, []);

  // Employment Hero's sign-in returns here as /settings?eh=connected|error&msg=…
  useEffect(() => {
    if (slug !== 'employment-hero') return;
    const q = new URLSearchParams(window.location.search);
    const result = q.get('eh');
    if (!result) return;
    if (result === 'connected') toast.success('Employment Hero connected');
    else toast.error(q.get('msg') || 'Employment Hero connection failed');
    q.delete('eh'); q.delete('msg');
    window.history.replaceState(null, '', window.location.pathname + (q.toString() ? `?${q}` : '') + window.location.hash);
  }, [slug]);

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      await api.post(`/integrations/${slug}/configure`, fields);
      toast.success(`${integration.label} credentials saved`);
      setConfigOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Save failed'); }
    finally { setSavingConfig(false); }
  };

  const connectOAuth = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get(`/integrations/${slug}/auth-url`);
      window.location.href = data.authUrl;
    } catch (e) { toast.error(e.response?.data?.message || 'Connect failed'); setConnecting(false); }
  };

  const mockConnect = async () => {
    setMockConnecting(true);
    try {
      await api.post(`/integrations/${slug}/mock-connect`);
      toast.success(`${integration.label} test mode active`);
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Test mode failed'); }
    finally { setMockConnecting(false); }
  };

  const disconnect = async () => {
    if (!await confirm(`Disconnect ${integration.label}?`, { title: `Disconnect ${integration.label}`, danger: true })) return;
    setDisconnecting(true);
    try {
      await api.post(`/integrations/${slug}/disconnect`);
      toast.success(`${integration.label} disconnected`);
      load();
    } catch (e) { toast.error('Disconnect failed'); }
    finally { setDisconnecting(false); }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/integrations/${slug}/sync`);
      toast.success(data.message);
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Sync failed'); }
    finally { setSyncing(false); }
  };

  const importCsv = async () => {
    if (!csvFile) return toast.error('Select a file first');
    setImporting(true);
    setCsvResult(null);
    try {
      const form = new FormData();
      form.append('file', csvFile);
      form.append('type', csvType);
      const { data } = await api.post('/integrations/csv-import/import', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCsvResult(data);
      toast.success(data.message);
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Import failed'); }
    finally { setImporting(false); }
  };

  const connected = status?.connected;
  const togglePass = (k) => setShowPasswords(p => ({ ...p, [k]: !p[k] }));

  return (
    <div className={clsx('rounded-xl border transition-all', connected ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200 bg-white')}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-xs font-bold shrink-0"
            style={{ backgroundColor: integration.color }}>
            {integration.abbr}
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-800">{integration.label}</p>
            <p className="text-xs text-slate-400">
              {connected
                ? `Connected${status?.tenantName ? ` to ${status.tenantName}` : ''}${status?.lastSync ? ` · Last sync: ${new Date(status.lastSync).toLocaleDateString('en-AU')} (${status.lastSyncCount})` : ''}`
                : integration.description}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          {connected ? (
            <>
              {integration.manageIn ? (
                <Link to={integration.manageIn.to}
                  className="flex items-center gap-1.5 px-3 py-1.5 border border-emerald-300 bg-white hover:bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium">
                  <ExternalLink size={12}/> {integration.manageIn.label}
                </Link>
              ) : integration.isCsv ? (
                <button onClick={() => setCsvOpen(o => !o)}
                  className="flex items-center gap-1.5 px-3 py-1.5 border border-indigo-300 bg-white hover:bg-indigo-50 text-indigo-700 rounded-lg text-xs font-medium">
                  <RefreshCw size={12}/> Import CSV
                </button>
              ) : (
                <button onClick={sync} disabled={syncing}
                  className="flex items-center gap-1.5 px-3 py-1.5 border border-emerald-300 bg-white hover:bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium disabled:opacity-50">
                  {syncing ? <Loader2 size={12} className="animate-spin"/> : <RefreshCw size={12}/>}
                  {integration.syncLabel || 'Sync'}
                </button>
              )}
              <button onClick={() => setConfigOpen(o => !o)} title="Update credentials"
                className="flex items-center gap-1 px-2.5 py-1.5 border border-slate-200 bg-white hover:bg-slate-50 text-slate-400 hover:text-slate-600 rounded-lg text-xs font-medium">
                <SettingsIcon size={12}/>
                <ChevronDown size={11} className={clsx('transition-transform', configOpen && 'rotate-180')}/>
              </button>
              <button onClick={disconnect} disabled={disconnecting}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 bg-white hover:bg-red-50 text-slate-500 hover:text-red-600 rounded-lg text-xs font-medium disabled:opacity-50">
                <Unlink size={12}/> Disconnect
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setConfigOpen(o => !o)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs text-slate-500 hover:bg-slate-50">
                <SettingsIcon size={11}/> Configure
                <ChevronDown size={11} className={clsx('transition-transform', configOpen && 'rotate-180')}/>
              </button>
              {integration.isOAuth ? (
                <button onClick={connectOAuth} disabled={connecting}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
                  style={{ backgroundColor: integration.color }}>
                  {connecting ? <Loader2 size={12} className="animate-spin"/> : <Link2 size={12}/>} Connect
                </button>
              ) : null}
            </>
          )}
        </div>
      </div>

      {/* Config Panel */}
      {configOpen && (
        <div className="border-t border-slate-200 px-4 py-4 space-y-3 bg-white rounded-b-xl">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{integration.label} Credentials</p>
          {integration.setupNote && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800 leading-relaxed">
              <p>{integration.setupNote}</p>
              {integration.setupLink && (
                <a href={integration.setupLink.href} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 mt-1.5 text-blue-600 font-medium hover:underline">
                  <ExternalLink size={10}/> {integration.setupLink.label}
                </a>
              )}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {integration.fields.map(f => (
              <div key={f.key}>
                <label className="input-label">{f.label}</label>
                {f.type === 'select' ? (
                  <select className="input-field" value={fields[f.key] || f.default || ''} onChange={e => setFields(p => ({ ...p, [f.key]: e.target.value }))}>
                    {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : f.type === 'password' ? (
                  <div className="relative">
                    <input className="input-field pr-8" type={showPasswords[f.key] ? 'text' : 'password'}
                      placeholder={f.placeholder || ''} value={fields[f.key] || ''}
                      onChange={e => setFields(p => ({ ...p, [f.key]: e.target.value }))}/>
                    <button type="button" onClick={() => togglePass(f.key)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400">
                      {showPasswords[f.key] ? <Eye size={13}/> : <EyeOff size={13}/>}
                    </button>
                  </div>
                ) : (
                  <input className="input-field" type="text" placeholder={f.placeholder || ''}
                    value={fields[f.key] || ''} onChange={e => setFields(p => ({ ...p, [f.key]: e.target.value }))}/>
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={() => setConfigOpen(false)} className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm text-slate-600">Cancel</button>
            <button onClick={saveConfig} disabled={savingConfig}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium disabled:opacity-60">
              {savingConfig ? <Loader2 size={12} className="animate-spin"/> : <Save size={12}/>} {connected ? 'Update Credentials' : 'Save Credentials'}
            </button>
          </div>
        </div>
      )}

      {/* CSV Import Panel */}
      {integration.isCsv && csvOpen && (
        <div className="border-t border-slate-200 px-4 py-4 space-y-3 bg-white rounded-b-xl">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Import from CSV</p>
          <div className="flex gap-3 items-center flex-wrap">
            <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
              {['clients','staff'].map(t => (
                <button key={t} onClick={() => setCsvType(t)}
                  className={clsx('px-3 py-1 text-xs font-medium rounded-md transition-all capitalize', csvType === t ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500')}>
                  {t}
                </button>
              ))}
            </div>
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={e => { setCsvFile(e.target.files[0]); setCsvResult(null); }}/>
            <button onClick={() => fileRef.current?.click()} className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded-lg text-xs text-slate-600 hover:bg-slate-50">
              <FileText size={12}/> {csvFile ? csvFile.name : 'Choose CSV file'}
            </button>
            <button onClick={importCsv} disabled={importing || !csvFile}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-medium disabled:opacity-60">
              {importing ? <Loader2 size={12} className="animate-spin"/> : <RefreshCw size={12}/>}
              {importing ? 'Importing…' : 'Import'}
            </button>
          </div>
          <p className="text-xs text-slate-400">
            {csvType === 'clients'
              ? 'Required columns: first_name, last_name. Optional: email, phone, ndis_number, address, date_of_birth, client_type'
              : 'Required columns: first_name, last_name, email. Optional: phone, role (carer/admin/office_user)'}
          </p>
          {csvResult && (
            <div className={clsx('p-3 rounded-lg text-xs', csvResult.errors?.length ? 'bg-yellow-50 border border-yellow-200' : 'bg-emerald-50 border border-emerald-200')}>
              <p className="font-semibold">{csvResult.message}</p>
              {csvResult.errors?.map((e, i) => <p key={i} className="text-slate-500 mt-1">{e}</p>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Notifications Section ────────────────────────────────────────────────────
// SMTP presets per email provider (just prefills the form; user can still edit)
const EMAIL_PROVIDERS = {
  custom:   { label: 'Custom SMTP', passLabel: 'Password' },
  resend:   { label: 'Resend',   host: 'smtp.resend.com',   port: 465, secure: true,  user: 'resend', passLabel: 'Resend API Key' },
  sendgrid: { label: 'SendGrid', host: 'smtp.sendgrid.net', port: 587, secure: false, user: 'apikey', passLabel: 'SendGrid API Key' },
  gmail:    { label: 'Gmail',    host: 'smtp.gmail.com',    port: 587, secure: false, passLabel: 'App Password' },
};

function NotificationsSection() {
  const [cfg, setCfg]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [testing, setTesting] = useState(false);
  const [running, setRunning] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/notifications/settings');
      setCfg(data);
    } catch { setCfg({ email: {}, notifications: {} }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (section, key, val) => setCfg(c => ({ ...c, [section]: { ...c[section], [key]: val } }));

  // Switch provider: prefill SMTP fields from the preset, but never clobber a
  // value the user already typed (only fill blanks). Always set the provider.
  const pickProvider = (prov) => {
    const preset = EMAIL_PROVIDERS[prov] || {};
    setCfg(c => {
      const e = { ...c.email, provider: prov };
      if (preset.host  !== undefined && !e.host) e.host = preset.host;
      if (preset.port  !== undefined && !e.port) e.port = preset.port;
      if (preset.user  !== undefined && !e.user) e.user = preset.user;
      if (preset.secure !== undefined && (e.secure === undefined || e.secure === null)) e.secure = preset.secure;
      return { ...c, email: e };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      // Don't send the masked placeholder back as a real password: when a pass
      // is already stored and the user didn't type a new one, drop `pass` so the
      // backend keeps the existing value.
      const email = { ...cfg.email };
      if (cfg.email?.has_pass && !email.pass) delete email.pass;
      delete email.has_pass;
      delete email.pass_last4;
      await api.put('/notifications/settings', { ...cfg, email });
      toast.success('Notification settings saved');
    } catch (e) { toast.error(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post('/notifications/test');
      toast.success(data.message || 'Test email sent');
    } catch (e) { toast.error(e.response?.data?.message || 'Test failed'); }
    finally { setTesting(false); }
  };

  const runDaily = async () => {
    setRunning(true);
    try {
      const { data } = await api.post('/notifications/run-daily');
      toast.success(data.message || 'Daily notifications triggered');
    } catch (e) { toast.error(e.response?.data?.message || 'Run failed'); }
    finally { setRunning(false); }
  };

  if (loading) return <div className="flex items-center gap-2 py-4 text-slate-400"><Loader2 size={16} className="animate-spin"/> Loading…</div>;

  const email = cfg?.email || {};
  const notif = cfg?.notifications || {};
  const mode  = email.mode || 'platform';
  const provider = email.provider || 'custom';
  const passLabel = (EMAIL_PROVIDERS[provider] || EMAIL_PROVIDERS.custom).passLabel;
  const passPlaceholder = email.has_pass
    ? `••••${email.pass_last4 || ''} — leave blank to keep`
    : '••••••••';

  return (
    <div className="space-y-6">
      {/* Email delivery */}
      <div className="space-y-4">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Email Delivery</p>

        {/* Mode chooser */}
        <div className="grid sm:grid-cols-2 gap-3">
          {[
            { val: 'platform', title: "Use Rostery's email (default)", desc: 'Zero setup — emails send immediately from Rostery.' },
            { val: 'own',      title: 'Use my own email server',        desc: 'Send through your own SMTP / provider.' },
          ].map(opt => (
            <button
              key={opt.val}
              type="button"
              onClick={() => set('email', 'mode', opt.val)}
              className={clsx(
                'text-left rounded-xl border p-4 transition-colors',
                mode === opt.val ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-200' : 'border-slate-200 hover:border-slate-300'
              )}
            >
              <div className="flex items-center gap-2">
                <span className={clsx('h-3.5 w-3.5 rounded-full border-2 shrink-0', mode === opt.val ? 'border-primary-600 bg-primary-600' : 'border-slate-300')} />
                <span className="text-sm font-semibold text-slate-800">{opt.title}</span>
              </div>
              <p className="text-xs text-slate-500 mt-1.5 ml-5.5">{opt.desc}</p>
            </button>
          ))}
        </div>

        {/* Platform mode: minimal fields */}
        {mode === 'platform' && (
          <div className="space-y-4">
            <p className="text-xs text-slate-500">Emails send from Rostery's server; replies go to your Reply-To address.</p>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="input-label">From Name <span className="text-slate-400 font-normal">(optional)</span></label><input className="input-field" placeholder="Rostery" value={email.fromName || ''} onChange={e => set('email','fromName',e.target.value)}/></div>
              <div><label className="input-label">Reply-To <span className="text-slate-400 font-normal">(optional)</span></label><input className="input-field" type="email" placeholder="replies@yourorg.com" value={email.replyTo || ''} onChange={e => set('email','replyTo',e.target.value)}/></div>
            </div>
          </div>
        )}

        {/* Own server mode: full SMTP config */}
        {mode === 'own' && (
          <div className="space-y-4">
            <div className="max-w-xs">
              <label className="input-label">Provider</label>
              <select className="input-field" value={provider} onChange={e => pickProvider(e.target.value)}>
                {Object.entries(EMAIL_PROVIDERS).map(([val, p]) => (
                  <option key={val} value={val}>{p.label}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="input-label">SMTP Host</label><input className="input-field" placeholder="smtp.gmail.com" value={email.host || ''} onChange={e => set('email','host',e.target.value)}/></div>
              <div><label className="input-label">SMTP Port</label><input className="input-field" type="number" placeholder="587" value={email.port || ''} onChange={e => set('email','port',Number(e.target.value))}/></div>
              <div><label className="input-label">Username / Email</label><input className="input-field" placeholder="notifications@yourorg.com" value={email.user || ''} onChange={e => set('email','user',e.target.value)}/></div>
              <div>
                <label className="input-label">{passLabel}</label>
                <div className="relative">
                  <input
                    className="input-field pr-9"
                    type={showPass ? 'text' : 'password'}
                    placeholder={passPlaceholder}
                    value={email.pass || ''}
                    onChange={e => set('email','pass',e.target.value)}
                  />
                  <button type="button" onClick={() => setShowPass(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                    {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>
              <div><label className="input-label">From Name</label><input className="input-field" placeholder="Rostery" value={email.fromName || ''} onChange={e => set('email','fromName',e.target.value)}/></div>
              <div><label className="input-label">From Address</label><input className="input-field" placeholder="no-reply@yourorg.com" value={email.fromAddress || ''} onChange={e => set('email','fromAddress',e.target.value)}/></div>
            </div>
            <div className="flex items-center gap-2">
              <Toggle checked={!!email.secure} onChange={v => set('email','secure',v)} />
              <span className="text-sm text-slate-600">Use TLS/SSL</span>
            </div>
          </div>
        )}
      </div>

      {/* Notification Triggers */}
      <div className="space-y-3">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Notification Triggers</p>
        {/* On unless switched off: pushes are free and they are how workers hear about their roster. */}
        <SettingRow label="Push notifications to workers" hint="New, changed, moved and cancelled shifts, the day-before reminder and the clock-in reminder, sent to the worker's phone.">
          <Toggle checked={notif.pushReminders !== false} onChange={v => set('notifications', 'pushReminders', v)} />
        </SettingRow>
        {[
          { key: 'shift_reminders',       label: 'Shift Reminders',       hint: 'Send staff a reminder the day before their shift' },
          { key: 'doc_expiry_7d',         label: 'Document Expiry (7 days)',  hint: 'Alert staff when documents expire within 7 days' },
          { key: 'doc_expiry_30d',        label: 'Document Expiry (30 days)', hint: 'Alert staff when documents expire within 30 days' },
          // defaultOn: the server sends these unless switched off, so "never saved" shows as on.
          { key: 'contract_notifications',label: 'Contract Sent',          hint: 'Notify staff when a contract is sent to them', defaultOn: true },
          { key: 'leave_decisions',       label: 'Leave Decisions',        hint: 'Notify staff when leave is approved or declined', defaultOn: true },
          { key: 'clockinReminder',       label: 'Clock-in reminder (to staff)', hint: 'Remind staff to clock in when their shift starts.' },
          { key: 'missedClockin',         label: 'Missed clock-in alert (to admins)', hint: "Alert admins when a shift wasn't clocked into." },
        ].map(({ key, label, hint, defaultOn }) => (
          <SettingRow key={key} label={label} hint={hint}>
            <Toggle checked={defaultOn ? notif[key] !== false : !!notif[key]} onChange={v => set('notifications', key, v)} />
          </SettingRow>
        ))}
        {notif.missedClockin && (
          <div className="flex items-center gap-3 pl-1 pt-1">
            <label className="text-sm text-slate-600">Alert after</label>
            <input
              className="input-field w-20"
              type="number" min={1}
              value={notif.missedClockinMinutes ?? 15}
              onChange={e => set('notifications','missedClockinMinutes', Number(e.target.value))}
            />
            <span className="text-sm text-slate-600">minutes.</span>
          </div>
        )}
      </div>

      {/* Forgotten clock-outs (backend lib/clockOutChecks.js): checked by Rostery every 5 minutes, so
          it works even when the worker's phone is off or flat. */}
      <div className="space-y-3">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Forgotten clock-outs</p>
        <p className="text-sm text-slate-500 -mt-1">Rostery checks every 5 minutes from the rostered shift, so it works even when a worker's phone is off or flat.</p>
        <SettingRow label="Remind the worker" hint="A push (or an email, when they have no phone registered) after the rostered finish: “Did you finish? Tap to clock out.”">
          <Toggle checked={notif.clockoutReminder !== false} onChange={v => set('notifications', 'clockoutReminder', v)} />
        </SettingRow>
        {notif.clockoutReminder !== false && <MinutesAfter label="Remind" value={notif.clockoutReminderMinutes ?? 15} onChange={v => set('notifications', 'clockoutReminderMinutes', v)} min={5} max={120} />}
        <SettingRow label="Tell the office" hint="A push and an email to admins and office staff, saying who has not clocked out of which shift.">
          <Toggle checked={notif.clockoutOfficeAlert !== false} onChange={v => set('notifications', 'clockoutOfficeAlert', v)} />
        </SettingRow>
        {notif.clockoutOfficeAlert !== false && <MinutesAfter label="Tell the office" value={notif.clockoutOfficeMinutes ?? 60} onChange={v => set('notifications', 'clockoutOfficeMinutes', v)} min={15} max={480} />}
        <SettingRow label="Clock them out automatically"
          hint="Closes the timesheet at the rostered finish (not the time it was noticed) and marks it “Auto clocked out”. It is not paid or billed until a manager checks the times in Timesheets › Review & approve.">
          <Toggle checked={notif.autoClockOut === true} onChange={v => set('notifications', 'autoClockOut', v)} />
        </SettingRow>
        {notif.autoClockOut === true && (
          <div className="space-y-3 pl-1">
            <MinutesAfter label="Clock out" value={notif.autoClockOutMinutes ?? 120} onChange={v => set('notifications', 'autoClockOutMinutes', v)} min={30} max={720} />
            <div className="flex items-center gap-3 flex-wrap">
              <label htmlFor="auto-clockout-time" className="text-sm text-slate-600">Clock-out time</label>
              <select id="auto-clockout-time" className="input-field w-auto" value={notif.autoClockOutTime === 'gps' ? 'gps' : 'rostered'}
                onChange={e => set('notifications', 'autoClockOutTime', e.target.value)}>
                <option value="rostered">The rostered finish time</option>
                <option value="gps">When GPS shows they left the client (else the rostered finish)</option>
              </select>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <label htmlFor="auto-clockout-cap" className="text-sm text-slate-600">With no rostered finish, close after</label>
              <input id="auto-clockout-cap" className="input-field w-20" type="number" min={8} max={24}
                value={notif.autoClockOutCapHours ?? 16} onChange={e => set('notifications', 'autoClockOutCapHours', Number(e.target.value))} />
              <span className="text-sm text-slate-600">hours from clock-in.</span>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-3 flex-wrap border-t border-slate-100 pt-4">
        <button onClick={save} disabled={saving} className="btn-primary btn-sm">
          {saving ? <Loader2 size={13} className="animate-spin"/> : <Save size={13}/>} Save Settings
        </button>
        <button onClick={test} disabled={testing} className="btn-secondary btn-sm">
          {testing ? <Loader2 size={13} className="animate-spin"/> : <Bell size={13}/>} Send Test Email
        </button>
        <button onClick={runDaily} disabled={running} className="btn-secondary btn-sm">
          {running ? <Loader2 size={13} className="animate-spin"/> : <RefreshCw size={13}/>} Run Daily Notifications Now
        </button>
      </div>
    </div>
  );
}

// "<label> [n] minutes after the rostered finish."
function MinutesAfter({ label, value, onChange, min, max }) {
  const id = `mins-${label.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <div className="flex items-center gap-3 pl-1 flex-wrap">
      <label htmlFor={id} className="text-sm text-slate-600">{label}</label>
      <input id={id} className="input-field w-20" type="number" min={min} max={max} value={value} onChange={e => onChange(Number(e.target.value))} />
      <span className="text-sm text-slate-600">minutes after the rostered finish.</span>
    </div>
  );
}

// ─── NAV sections ─────────────────────────────────────────────────────────────
const NAV_GROUPS = [
  { group: 'Organisation', items: [
    { id: 'general',          label: 'General',                 icon: Building },
    { id: 'service_types',    label: 'Service Types',           icon: Tag },
    { id: 'job_titles',       label: 'Job Titles',              icon: Briefcase },
    { id: 'teams',            label: 'Teams',                   icon: Users },
  ]},
  { group: 'Scheduling', items: [
    { id: 'scheduler',        label: 'Scheduler',               icon: Calendar },
    { id: 'group_ratios',     label: 'Group Ratios',            icon: Users },
    { id: 'travel_transport', label: 'Travel & Transport',      icon: Car },
    { id: 'time_attendance',  label: 'Time & Attendance',       icon: Clock },
    { id: 'declined_shifts',  label: 'Declined Shift Flow',     icon: AlertCircle },
  ]},
  { group: 'Compliance & Records', items: [
    { id: 'compliance_reqs',  label: 'Compliance Requirements', icon: Shield },
    { id: 'cancellations',    label: 'Cancellation Policies',   icon: List },
    { id: 'document_cats',    label: 'Document Categories',     icon: FileText },
    { id: 'note_types',       label: 'Note Types',              icon: List },
    { id: 'public_holidays',  label: 'Public Holidays',         icon: Globe },
  ]},
  { group: 'Finance', items: [
    { id: 'price_books',      label: 'Price Books',             icon: BookOpen },
    { id: 'financial_config', label: 'Financial Config',        icon: DollarSign },
  ]},
  { group: 'System', items: [
    { id: 'integrations',     label: 'Integrations',            icon: Zap },
    { id: 'notifications',    label: 'Notifications',           icon: Bell },
    { id: 'broadcast',        label: 'Send a notification',     icon: Send },
    { id: 'security',         label: 'Security & MFA',          icon: KeyRound },
  ]},
];
const NAV_SECTIONS = NAV_GROUPS.flatMap(g => g.items);

// Which settings each section's Save button writes (drives the unsaved-changes state).
const SECTION_FIELDS = {
  general: ['company_name', 'company_country', 'timezone', 'show_local_features', 'enable_mileage_start', 'require_2fa'],
  scheduler: ['pay_run_frequency', 'pay_run_start_day', 'communication_mode', 'staff_can_manage_shifts', 'share_client_info',
    'worker_events_enabled', 'worker_events_days', 'allow_conflicting_shifts', 'carer_qual_matching',
    'default_verification_status', 'recurring_shift_duration', 'client_types'],
  time_attendance: ['enable_unavailability', 'unavailability_notice_period', 'checkin_location_check', 'attendance_threshold',
    'auto_approve_clockin', 'timesheet_precision', 'pay_rate_based_on', 'late_clockin_notification',
    'require_progress_note', 'payroll_software'],
  declined_shifts: ['decline_assigned_shifts', 'declined_to_job_board'],
};

// ─── Main Settings page ───────────────────────────────────────────────────────
export default function Settings() {
  const { user } = useAuth();
  const isSuper = user?.role === 'super_admin';
  // Accounting and payroll integrations (Xero, QuickBooks, Employment Hero) show in every org;
  // the rest are platform-managed and only super admins see them.
  const ORG_INTEGRATIONS = ['employment_hero'];
  const otherIntegrations = isSuper
    ? OTHER_INTEGRATIONS
    : OTHER_INTEGRATIONS.filter(i => ORG_INTEGRATIONS.includes(i.key));
  const [activeSection, setActiveSection] = useState('general');
  const [navQuery, setNavQuery]           = useState('');
  const [loading, setLoading]             = useState(true);
  const [saving, setSaving]               = useState({});

  // Global settings state
  const [cfg, setCfg] = useState({
    company_name: '', company_country: 'Australia', timezone: 'Australia/Sydney',
    require_2fa: false, allow_role_edit: true, show_local_features: true,
    enable_mileage_start: false, pay_run_frequency: 'weekly', pay_run_start_day: 'monday',
    communication_mode: 'email', staff_can_manage_shifts: false, share_client_info: true,
    worker_events_enabled: true, worker_events_days: 28,
    allow_conflicting_shifts: false, carer_qual_matching: 'warning',
    default_verification_status: 'yes', recurring_shift_duration: '3_monthly',
    enable_unavailability: true, unavailability_notice_period: 3,
    checkin_location_check: false, attendance_threshold: 15,
    auto_approve_clockin: false, timesheet_precision: 3, pay_rate_based_on: 'end_time',
    late_clockin_notification: false, payroll_software: '',
    decline_assigned_shifts: false, declined_to_job_board: false,
    client_types: ['ndis_managed', 'plan_managed', 'self_managed'],
    integrations: {},
  });

  // Sub-list states
  const [shiftTypes, setShiftTypes]           = useState([]);
  const [docCats, setDocCats]                 = useState([]);
  const [noteTypes, setNoteTypes]             = useState([]);
  const [publicHolidays, setPublicHolidays]   = useState([]);
  const [jobTitles, setJobTitles]             = useState([]);

  // Input staging
  const [newShiftType, setNewShiftType]       = useState('');
  const [newDocCat, setNewDocCat]             = useState('');
  const [docTab, setDocTab]                   = useState('client');
  const [newJobTitle, setNewJobTitle]         = useState('');
  const [newHoliday, setNewHoliday]           = useState({ name: '', date: '' });
  const [showAddHoliday, setShowAddHoliday]   = useState(false);

  // The last saved values, to tell which sections have unsaved edits.
  const [savedCfg, setSavedCfg] = useState(null);

  const mainRef = useRef(null);
  const location = useLocation();

  // ── Handle OAuth callback redirects ───────────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('xero') === 'connected') toast.success('Xero connected successfully!');
    if (params.get('xero') === 'error') toast.error(`Xero error: ${params.get('msg') || 'Unknown'}`);
    if (params.get('qb') === 'connected') toast.success('QuickBooks connected successfully!');
    if (params.get('qb') === 'error') toast.error(`QuickBooks error: ${params.get('msg') || 'Unknown'}`);
    if (params.get('gc') === 'connected') toast.success('Google Calendar connected successfully!');
    if (params.get('gc') === 'error') toast.error(`Google Calendar error: ${params.get('msg') || 'Unknown'}`);
  }, [location.search]);

  // ── Load all settings ──────────────────────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    api.get('/settings')
      .then(r => {
        const d = r.data;
        if (d.settings && Object.keys(d.settings).length) {
          setCfg(prev => ({
            ...prev,
            ...d.settings,
            integrations: d.settings.integrations || {},
          }));
        }
        setShiftTypes(d.shiftTypes || []);
        setDocCats(d.documentCategories || []);
        setNoteTypes(d.noteTypes || []);
        setPublicHolidays(d.publicHolidays || []);
        setJobTitles(d.jobTitles || []);
      })
      .catch(() => toast.error('Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  // ── Save helper ────────────────────────────────────────────────────────────
  const saveSection = useCallback(async (sectionId, fields) => {
    setSaving(s => ({ ...s, [sectionId]: true }));
    try {
      await api.put('/settings', fields);
      // What was sent is now the saved state (a few fields are normalised on the way out).
      setCfg(c => ({ ...c, ...fields }));
      setSavedCfg(sv => ({ ...(sv || {}), ...fields }));
      toast.success('Saved');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(s => ({ ...s, [sectionId]: false }));
    }
  }, []);

  // ── Scroll-to section (sidebar click) ─────────────────────────────────────
  const scrollTo = (id) => {
    setActiveSection(id);
    const el = document.getElementById(`sec-${id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ?section=<id> in the address opens the page on that section.
  useEffect(() => {
    if (loading) return;
    const id = new URLSearchParams(location.search).get('section');
    if (id && NAV_SECTIONS.some((s) => s.id === id)) setTimeout(() => scrollTo(id), 150);
  }, [loading, location.search]); // eslint-disable-line

  // ── Intersection observer for active nav ──────────────────────────────────
  useEffect(() => {
    const observers = [];
    NAV_SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(`sec-${id}`);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActiveSection(id); },
        { rootMargin: '-30% 0px -60% 0px' }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach(o => o.disconnect());
  }, [loading]);

  // ── Unsaved-changes tracking ──────────────────────────────────────────────
  useEffect(() => { if (!loading && savedCfg === null) setSavedCfg(cfg); }, [loading]);
  const cmp = (k, v) => {
    if (k === 'worker_events_enabled') return String(v !== false);          // unset means on
    if (k === 'worker_events_days') return String(Number(v) || 28);
    if (v == null || v === false || v === '') return '';
    return Array.isArray(v) ? JSON.stringify([...v].sort()) : String(v);
  };
  const isDirty = (id) => !!savedCfg && (SECTION_FIELDS[id] || []).some(k => cmp(k, cfg[k]) !== cmp(k, savedCfg[k]));
  const anyDirty = Object.keys(SECTION_FIELDS).some(isDirty);
  useEffect(() => {
    if (!anyDirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [anyDirty]);

  // Sections with unsaved edits, in page order. The bottom bar names the first one; its Save and
  // Discard act on that section only.
  const dirtyIds = NAV_SECTIONS.map(s => s.id).filter(isDirty);
  // Put a section's fields back to the last saved values — the same values isDirty compares
  // against, so the section reads clean again. A field the server never sent is removed again.
  const discardSection = (id) => {
    if (!savedCfg) return;
    setCfg(c => {
      const next = { ...c };
      (SECTION_FIELDS[id] || []).forEach(k => {
        if (Object.prototype.hasOwnProperty.call(savedCfg, k)) next[k] = savedCfg[k];
        else delete next[k];
      });
      return next;
    });
  };
  // When the bar first appears it must not land on top of the field being typed in.
  const saveBarRef = useRef(null);
  const saveBarShown = dirtyIds.length > 0;
  useEffect(() => {
    if (!saveBarShown) return;
    const bar = saveBarRef.current; const el = document.activeElement;
    if (!bar || !el || el === document.body || bar.contains(el)) return;
    const b = bar.getBoundingClientRect(); const r = el.getBoundingClientRect();
    if (r.bottom > b.top && r.top < b.bottom) window.scrollBy(0, r.bottom - b.top + 16);
  }, [saveBarShown]);

  // ── Toggle cfg field ───────────────────────────────────────────────────────
  const toggle = (field) => setCfg(c => ({ ...c, [field]: !c[field] }));
  const setField = (field, val) => setCfg(c => ({ ...c, [field]: val }));

  // ── Section saves ──────────────────────────────────────────────────────────
  // Each section's one save call. Its card's Save button and the bottom bar both call this, so
  // they always send exactly the same fields, normalised the same way.
  const sectionSave = {
    general: () => saveSection('general', {
      company_name: cfg.company_name,
      company_country: cfg.company_country,
      timezone: cfg.timezone,
      show_local_features: cfg.show_local_features,
      enable_mileage_start: cfg.enable_mileage_start,
      require_2fa: cfg.require_2fa,
    }),
    scheduler: () => saveSection('scheduler', {
      pay_run_frequency: cfg.pay_run_frequency,
      pay_run_start_day: cfg.pay_run_start_day,
      communication_mode: cfg.communication_mode,
      staff_can_manage_shifts: cfg.staff_can_manage_shifts,
      share_client_info: cfg.share_client_info,
      worker_events_enabled: cfg.worker_events_enabled !== false,
      worker_events_days: Number(cfg.worker_events_days) || 28,
      allow_conflicting_shifts: cfg.allow_conflicting_shifts,
      carer_qual_matching: cfg.carer_qual_matching,
      default_verification_status: cfg.default_verification_status,
      recurring_shift_duration: cfg.recurring_shift_duration,
      client_types: cfg.client_types,
    }),
    time_attendance: () => saveSection('time_attendance', {
      enable_unavailability: cfg.enable_unavailability,
      unavailability_notice_period: cfg.unavailability_notice_period,
      checkin_location_check: cfg.checkin_location_check,
      attendance_threshold: cfg.attendance_threshold,
      auto_approve_clockin: cfg.auto_approve_clockin,
      timesheet_precision: cfg.timesheet_precision,
      pay_rate_based_on: cfg.pay_rate_based_on,
      late_clockin_notification: cfg.late_clockin_notification,
      require_progress_note: cfg.require_progress_note,
      payroll_software: cfg.payroll_software,
    }),
    declined_shifts: () => saveSection('declined_shifts', {
      decline_assigned_shifts: cfg.decline_assigned_shifts,
      declined_to_job_board: cfg.declined_to_job_board,
    }),
  };

  // ── Shift Types CRUD ───────────────────────────────────────────────────────
  const addShiftType = async () => {
    if (!newShiftType.trim()) return;
    try {
      const r = await api.post('/settings/shift-types', { name: newShiftType.trim() });
      setShiftTypes(p => [...p, r.data]);
      setNewShiftType('');
      toast.success('Shift type added');
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  const deleteShiftType = async (id) => {
    try {
      await api.delete(`/settings/shift-types/${id}`);
      setShiftTypes(p => p.filter(t => t.id !== id));
      toast.success('Removed');
    } catch { toast.error('Failed'); }
  };

  const toggleShiftType = async (st) => {
    try {
      const r = await api.put(`/settings/shift-types/${st.id}`, { is_active: !st.is_active });
      setShiftTypes(p => p.map(t => t.id === st.id ? r.data : t));
    } catch { toast.error('Failed'); }
  };

  // ── Document Categories CRUD ───────────────────────────────────────────────
  const addDocCat = async () => {
    if (!newDocCat.trim()) return;
    try {
      const r = await api.post('/settings/document-categories', { name: newDocCat.trim(), applies_to: docTab });
      setDocCats(p => [...p, r.data]);
      setNewDocCat('');
      toast.success('Category added');
    } catch { toast.error('Failed'); }
  };

  const deleteDocCat = async (id) => {
    try {
      await api.delete(`/settings/document-categories/${id}`);
      setDocCats(p => p.filter(c => c.id !== id));
      toast.success('Removed');
    } catch { toast.error('Failed'); }
  };

  // ── Note Types update ──────────────────────────────────────────────────────
  const updateNoteType = async (id, field, value) => {
    try {
      const r = await api.put(`/settings/note-types/${id}`, { [field]: value });
      setNoteTypes(p => p.map(n => n.id === id ? r.data : n));
    } catch { toast.error('Failed'); }
  };

  // ── Public Holidays CRUD ───────────────────────────────────────────────────
  const addHoliday = async () => {
    if (!newHoliday.name.trim() || !newHoliday.date) return toast.error('Name and date required');
    try {
      const r = await api.post('/settings/public-holidays', newHoliday);
      setPublicHolidays(p => [...p, r.data].sort((a, b) => a.date.localeCompare(b.date)));
      setNewHoliday({ name: '', date: '' });
      setShowAddHoliday(false);
      toast.success('Holiday added');
    } catch { toast.error('Failed'); }
  };

  const deleteHoliday = async (id) => {
    try {
      await api.delete(`/settings/public-holidays/${id}`);
      setPublicHolidays(p => p.filter(h => h.id !== id));
      toast.success('Removed');
    } catch (e) { toast.error(e.response?.data?.message || 'Cannot delete'); }
  };

  // ── Job Titles CRUD ────────────────────────────────────────────────────────
  const addJobTitle = async () => {
    if (!newJobTitle.trim()) return;
    try {
      const r = await api.post('/settings/job-titles', { name: newJobTitle.trim() });
      setJobTitles(p => [...p, r.data]);
      setNewJobTitle('');
      toast.success('Job title added');
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  const deleteJobTitle = async (id) => {
    try {
      await api.delete(`/settings/job-titles/${id}`);
      setJobTitles(p => p.filter(j => j.id !== id));
      toast.success('Removed');
    } catch { toast.error('Failed'); }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) {
    // The page's own shape while settings load, rather than a spinner in empty space.
    return (
      <SettingsPage className="max-w-none">
        <div className="space-y-2" aria-busy="true" aria-label="Loading settings">
          <div className="h-6 w-40 rounded-md bg-slate-200/70 motion-safe:animate-pulse" />
          <div className="h-4 w-96 max-w-full rounded-md bg-slate-100 motion-safe:animate-pulse" />
        </div>
        <div className="flex items-start gap-8">
          <div className="hidden w-60 shrink-0 space-y-2 lg:block">
            {Array.from({ length: 9 }).map((_, i) => <div key={i} className="h-8 rounded-lg bg-slate-100 motion-safe:animate-pulse" />)}
          </div>
          <div className="min-w-0 flex-1 space-y-6">
            {[0, 1].map(i => (
              <div key={i} className="card p-6">
                <div className="h-5 w-48 rounded-md bg-slate-200/70 motion-safe:animate-pulse" />
                <div className="mt-6 grid gap-4 md:grid-cols-2">
                  {Array.from({ length: 4 }).map((_, j) => <div key={j} className="h-10 rounded-xl bg-slate-100 motion-safe:animate-pulse" />)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </SettingsPage>
    );
  }

  const filteredDocCats = docCats.filter(c => c.applies_to === docTab);

  const q = navQuery.trim().toLowerCase();
  const navGroups = NAV_GROUPS
    .map(g => ({ ...g, items: q ? g.items.filter(i => i.label.toLowerCase().includes(q)) : g.items }))
    .filter(g => g.items.length > 0);

  return (
    <SettingsPage className="max-w-none">
      {/* This page scrolls through every section, so it is named for all of them; "General" is only
          the first card. The top bar already says "Settings / Configure your workspace". */}
      <SettingsHeader
        title="Workspace settings"
        description={`Organisation, scheduling, compliance, finance and system preferences${cfg.company_name ? ` for ${cfg.company_name}` : ''}.`}
      />

      {/* ── Small screens: search + section chips ─────────────────────── */}
      <div className="space-y-3 lg:hidden">
        <div className="relative">
            <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={navQuery} onChange={e => setNavQuery(e.target.value)} placeholder="Find a setting"
              aria-label="Find a setting" className="input-field !py-2 pl-9 pr-8" />
            {navQuery && (
              <button onClick={() => setNavQuery('')} aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:text-slate-600">
                <X size={14} />
              </button>
            )}
        </div>
        <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {navGroups.flatMap(g => g.items).map(({ id, label, icon: Icon }) => {
            const active = activeSection === id;
            return (
              <button key={id} onClick={() => scrollTo(id)} aria-current={active ? 'true' : undefined}
                className={clsx(
                  'flex shrink-0 items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-medium transition-colors',
                  active ? 'border-primary-200 bg-primary-50 text-primary-700' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                )}>
                <Icon size={13} />
                {label}
                {isDirty(id) && <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-label="unsaved changes" />}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex items-start gap-8">
        {/* ── Section nav ──────────────────────────────────────────────────── */}
        <nav aria-label="Settings sections"
          className="sticky top-[88px] hidden max-h-[calc(100vh-112px)] w-60 shrink-0 overflow-y-auto pb-6 pr-1 lg:block">
          <div className="relative mb-5">
            <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={navQuery} onChange={e => setNavQuery(e.target.value)} placeholder="Find a setting"
              aria-label="Find a setting" className="input-field !py-2 pl-9 pr-8" />
            {navQuery && (
              <button onClick={() => setNavQuery('')} aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:text-slate-600">
                <X size={14} />
              </button>
            )}
          </div>
          <div className="space-y-5">
            {navGroups.map(({ group, items }) => (
              <div key={group}>
                <p className="px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-500">{group}</p>
                <div className="space-y-px">
                  {items.map(({ id, label, icon: Icon }) => {
                    const active = activeSection === id;
                    return (
                      <button key={id} onClick={() => scrollTo(id)} aria-current={active ? 'true' : undefined}
                        className={clsx(
                          'flex w-full items-center gap-2.5 rounded-lg px-3 py-[7px] text-left text-[13px] font-medium transition-colors duration-150',
                          active
                            ? 'bg-white text-slate-900 shadow-[0_1px_2px_rgba(16,24,40,.06)] ring-1 ring-slate-200/80'
                            : 'text-slate-600 hover:bg-slate-100/80 hover:text-slate-900'
                        )}>
                        <Icon size={15} strokeWidth={1.9} className={clsx('shrink-0', active ? 'text-primary-600' : 'text-slate-400')} />
                        <span className="min-w-0 flex-1 truncate">{label}</span>
                        {isDirty(id) && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" title="Unsaved changes" aria-label="unsaved changes" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            {navGroups.length === 0 && (
              <p className="px-3 py-2 text-sm text-slate-500">No settings match &ldquo;{navQuery}&rdquo;.</p>
            )}
          </div>
        </nav>

        {/* ── Sections (same order as the nav) ─────────────────────────────── */}
        <div ref={mainRef} className="min-w-0 flex-1 space-y-6">
          {/* ── GENERAL ─────────────────────────────────────────────────── */}
          <div id="sec-general" className="scroll-mt-[88px]">
            <Section title="General" onSave={sectionSave.general} saving={saving.general} dirty={isDirty('general')}>
              <div className="space-y-6">
                {/* ── Organisation identity ─────────────────────────────── */}
                <div>
                  <GroupHeading>Organisation</GroupHeading>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label htmlFor="set-company-name" className="input-label">Company name</label>
                      <input id="set-company-name" className="input-field" value={cfg.company_name}
                        onChange={e => setField('company_name', e.target.value)} />
                    </div>
                    <div>
                      <label htmlFor="set-country" className="input-label">Country</label>
                      <select id="set-country" className="input-field" value={cfg.company_country}
                        onChange={e => setField('company_country', e.target.value)}>
                        {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="set-timezone" className="input-label">Timezone</label>
                      <select id="set-timezone" className="input-field" value={cfg.timezone}
                        onChange={e => setField('timezone', e.target.value)}>
                        {TIMEZONES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>
                </div>

                {/* ── Security & preferences ────────────────────────────── */}
                <div>
                  <GroupHeading>Security &amp; preferences</GroupHeading>
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {[
                      { key: 'require_2fa',          icon: Lock,  label: 'Two-factor authentication', hint: 'All admin and office users must use 2FA' },
                      { key: 'enable_mileage_start', icon: Car,   label: 'Mileage tracking',          hint: 'Allow staff to log mileage on shifts' },
                      { key: 'show_local_features',  icon: Globe, label: 'Local features',            hint: 'Display region-specific functionality' },
                    ].map(({ key, icon: Icon, label, hint }) => (
                      // The whole card is the switch.
                      <button key={key} type="button" role="switch" aria-checked={!!cfg[key]}
                        onClick={() => setField(key, !cfg[key])}
                        className={clsx(
                          'flex items-start gap-3 rounded-xl border p-4 text-left transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
                          cfg[key] ? 'border-primary-200 bg-primary-50/50' : 'border-slate-200/80 bg-white hover:border-slate-300 hover:bg-slate-50/60'
                        )}>
                        <span className={clsx('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset',
                          cfg[key] ? 'bg-white text-primary-600 ring-primary-100' : 'bg-slate-50 text-slate-500 ring-slate-200/80')}>
                          <Icon size={16} strokeWidth={1.9} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center justify-between gap-2">
                            <span className="text-sm font-semibold leading-tight text-slate-800">{label}</span>
                            <Switch checked={!!cfg[key]} decorative />
                          </span>
                          <span className="mt-1 block text-xs leading-snug text-slate-500">{hint}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </Section>
          </div>

          {/* ── SERVICE TYPES (auto-populated hub — Brevity model) ──────────── */}
          <div id="sec-service_types" className="scroll-mt-[88px]">
            <Section title="Service Types">
              <ServiceTypes />
            </Section>
          </div>

          {/* ── JOB TITLES ───────────────────────────────────────────────── */}
          <div id="sec-job_titles" className="scroll-mt-[88px]">
            <Section title="Job Titles">
              <AddTagInput
                value={newJobTitle}
                onChange={setNewJobTitle}
                onAdd={addJobTitle}
                placeholder="New job title…"
              />
              <TagList items={jobTitles} onDelete={deleteJobTitle} />
              {jobTitles.length === 0 && <p className="text-sm text-slate-400">No job titles yet.</p>}
            </Section>
          </div>

          {/* ── TEAMS ────────────────────────────────────────────────────── */}
          <div id="sec-teams" className="scroll-mt-[88px]">
            <Section title="Teams">
              <Teams />
            </Section>
          </div>

          {/* ── SCHEDULER ────────────────────────────────────────────────── */}
          <div id="sec-scheduler" className="scroll-mt-[88px]">
            <Section title="Scheduler" onSave={sectionSave.scheduler} saving={saving.scheduler} dirty={isDirty('scheduler')}>

              {/* Client types */}
              <fieldset>
                <legend className="input-label mb-2">Client types</legend>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 sm:grid-cols-3 lg:grid-cols-4">
                  {[
                    { val: 'level1_aged_care',  label: 'Level 1 Aged Care' },
                    { val: 'level2_aged_care',  label: 'Level 2 Aged Care' },
                    { val: 'level3_aged_care',  label: 'Level 3 Aged Care' },
                    { val: 'level4_aged_care',  label: 'Level 4 Aged Care' },
                    { val: 'ndis_managed',      label: 'NDIS Managed' },
                    { val: 'plan_managed',      label: 'Plan Managed' },
                    { val: 'self_managed',      label: 'Self Managed' },
                    { val: 'all',               label: 'All' },
                  ].map(({ val, label }) => {
                    const checked = (cfg.client_types || []).includes(val);
                    return (
                      <label key={val} className="flex cursor-pointer select-none items-center gap-2 text-sm text-slate-700">
                        <input type="checkbox" checked={checked} onChange={() => {
                          const ct = cfg.client_types || [];
                          setField('client_types', checked ? ct.filter(v => v !== val) : [...ct, val]);
                        }} className="rounded border-slate-300 text-primary-600 focus:ring-primary-500" />
                        {label}
                      </label>
                    );
                  })}
                </div>
              </fieldset>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="input-label">Pay run frequency</label>
                  <select className="input-field" value={cfg.pay_run_frequency}
                    onChange={e => setField('pay_run_frequency', e.target.value)}>
                    <option value="weekly">Weekly</option>
                    <option value="fortnightly">Fortnightly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                <div>
                  <label className="input-label">Pay run start day</label>
                  <select className="input-field" value={cfg.pay_run_start_day}
                    onChange={e => setField('pay_run_start_day', e.target.value)}>
                    {['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].map(d => (
                      <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="input-label">Communication mode</label>
                  <select className="input-field" value={cfg.communication_mode}
                    onChange={e => setField('communication_mode', e.target.value)}>
                    <option value="email">Email</option>
                    <option value="sms">SMS</option>
                    <option value="both">Both</option>
                  </select>
                </div>
                <div>
                  <label className="input-label">Carer qualification matching</label>
                  <select className="input-field" value={cfg.carer_qual_matching}
                    onChange={e => setField('carer_qual_matching', e.target.value)}>
                    <option value="strict">Strict</option>
                    <option value="warning">Warning</option>
                    <option value="none">None</option>
                  </select>
                </div>
                <div>
                  <label className="input-label">Default verification status</label>
                  <select className="input-field" value={cfg.default_verification_status}
                    onChange={e => setField('default_verification_status', e.target.value)}>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
                <div>
                  <label className="input-label">Recurring shift duration</label>
                  <select className="input-field" value={cfg.recurring_shift_duration}
                    onChange={e => setField('recurring_shift_duration', e.target.value)}>
                    <option value="1_monthly">1 Monthly</option>
                    <option value="3_monthly">3 Monthly</option>
                    <option value="6_monthly">6 Monthly</option>
                    <option value="12_monthly">12 Monthly</option>
                  </select>
                </div>
              </div>

              <SettingGroup>
                <SettingRow label="Staff can manage shifts" hint="Allow staff to create and edit their own shifts">
                  <Toggle label="Staff can manage shifts" checked={cfg.staff_can_manage_shifts} onChange={v => setField('staff_can_manage_shifts', v)} />
                </SettingRow>
                <SettingRow label="Share client info with carer" hint="Show client profile details to assigned carer">
                  <Toggle label="Share client info with carer" checked={cfg.share_client_info} onChange={v => setField('share_client_info', v)} />
                </SettingRow>
                <SettingRow label="Show client events in the app" hint="The Events tab on a shift: the client's notes and incidents from all staff. Turn off to hide it from support workers.">
                  <Toggle label="Show client events in the app" checked={cfg.worker_events_enabled !== false} onChange={v => setField('worker_events_enabled', v)} />
                </SettingRow>
                {cfg.worker_events_enabled !== false && (
                  <SettingRow label="Events workers can see" hint="Up to the day of the shift, going back this far">
                    <select value={Number(cfg.worker_events_days) || 28} onChange={e => setField('worker_events_days', Number(e.target.value))}
                      className="input-field !w-auto text-sm">
                      <option value={1}>The shift day only</option>
                      <option value={7}>Last 7 days</option>
                      <option value={14}>Last 14 days</option>
                      <option value={28}>Last 28 days</option>
                      <option value={60}>Last 60 days</option>
                      <option value={90}>Last 90 days</option>
                    </select>
                  </SettingRow>
                )}
                <SettingRow label="Allow conflicting shifts" hint="Allow the same staff to be booked in overlapping shifts">
                  <Toggle label="Allow conflicting shifts" checked={cfg.allow_conflicting_shifts} onChange={v => setField('allow_conflicting_shifts', v)} />
                </SettingRow>
              </SettingGroup>
            </Section>
          </div>

          {/* ── GROUP RATIOS ────────────────────────────────────────────── */}
          <div id="sec-group_ratios" className="scroll-mt-[88px]">
            <Section title="Group Ratios">
              <ServiceRatios />
            </Section>
          </div>

          {/* ── TRAVEL & TRANSPORT ──────────────────────────────────────── */}
          <div id="sec-travel_transport" className="scroll-mt-[88px]">
            <Section title="Travel & Transport">
              <TravelTransport embedded />
            </Section>
          </div>

          {/* ── TIME & ATTENDANCE ─────────────────────────────────────────── */}
          <div id="sec-time_attendance" className="scroll-mt-[88px]">
            <Section title="Time & Attendance" onSave={sectionSave.time_attendance} saving={saving.time_attendance} dirty={isDirty('time_attendance')}>

              <SettingGroup>
                <SettingRow label="Enable unavailability" hint="Allow staff to submit unavailability requests">
                  <Toggle label="Enable unavailability" checked={cfg.enable_unavailability} onChange={v => setField('enable_unavailability', v)} />
                </SettingRow>
                <SettingRow label="Unavailability notice period (days)" hint="Minimum days notice required for unavailability">
                  <input type="number" min="0" className="input-field !w-20 text-center"
                    value={cfg.unavailability_notice_period}
                    onChange={e => setField('unavailability_notice_period', parseInt(e.target.value) || 0)} />
                </SettingRow>
                <SettingRow label="Check-in location check" hint="Verify staff location on check-in">
                  <Toggle label="Check-in location check" checked={cfg.checkin_location_check} onChange={v => setField('checkin_location_check', v)} />
                </SettingRow>
                <SettingRow label="Attendance threshold (minutes)" hint="Grace period for late clock-in/out">
                  <input type="number" min="0" className="input-field !w-20 text-center"
                    value={cfg.attendance_threshold}
                    onChange={e => setField('attendance_threshold', parseInt(e.target.value) || 0)} />
                </SettingRow>
                <SettingRow label="Auto-approve shift if clock-in/out successful">
                  <Toggle label="Auto-approve shift if clock-in/out successful" checked={cfg.auto_approve_clockin} onChange={v => setField('auto_approve_clockin', v)} />
                </SettingRow>
                <SettingRow label="Late clock-in notification">
                  <Toggle label="Late clock-in notification" checked={cfg.late_clockin_notification} onChange={v => setField('late_clockin_notification', v)} />
                </SettingRow>
                <SettingRow label="Require progress note before clock-out" hint="Staff must submit a progress note before they can clock out of a shift">
                  <Toggle label="Require progress note before clock-out" checked={cfg.require_progress_note} onChange={v => setField('require_progress_note', v)} />
                </SettingRow>
              </SettingGroup>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="input-label">Timesheet precision (decimals)</label>
                  <select className="input-field" value={cfg.timesheet_precision}
                    onChange={e => setField('timesheet_precision', parseInt(e.target.value))}>
                    <option value={1}>1</option>
                    <option value={2}>2</option>
                    <option value={3}>3</option>
                  </select>
                </div>
                <div>
                  <label className="input-label">Pay rate based on</label>
                  <select className="input-field" value={cfg.pay_rate_based_on}
                    onChange={e => setField('pay_rate_based_on', e.target.value)}>
                    <option value="end_time">End Time</option>
                    <option value="start_time">Start Time</option>
                  </select>
                </div>
                <div>
                  <label className="input-label">Payroll software</label>
                  <input className="input-field" placeholder="e.g. Xero, MYOB…"
                    value={cfg.payroll_software || ''}
                    onChange={e => setField('payroll_software', e.target.value)} />
                </div>
              </div>
            </Section>
          </div>

          {/* ── DECLINED SHIFT FLOW ──────────────────────────────────────── */}
          <div id="sec-declined_shifts" className="scroll-mt-[88px]">
            <Section title="Declined Shift Flow" onSave={sectionSave.declined_shifts} saving={saving.declined_shifts} dirty={isDirty('declined_shifts')}>
              <SettingGroup>
                <SettingRow label="Allow staff to decline assigned shifts">
                  <Toggle label="Allow staff to decline assigned shifts" checked={cfg.decline_assigned_shifts} onChange={v => setField('decline_assigned_shifts', v)} />
                </SettingRow>
                <SettingRow label="Declined shifts automatically added to Job Board">
                  <Toggle label="Declined shifts automatically added to Job Board" checked={cfg.declined_to_job_board} onChange={v => setField('declined_to_job_board', v)} />
                </SettingRow>
              </SettingGroup>
            </Section>
          </div>

          {/* ── COMPLIANCE REQUIREMENTS ─────────────────────────────────── */}
          <div id="sec-compliance_reqs" className="scroll-mt-[88px]">
            <Section title="Compliance Requirements">
              <ComplianceRequirements />
            </Section>
          </div>

          {/* ── CANCELLATION POLICIES ───────────────────────────────────── */}
          <div id="sec-cancellations" className="scroll-mt-[88px]">
            <Section title="Cancellation Policies">
              <CancellationPolicies />
            </Section>
          </div>

          {/* ── DOCUMENT CATEGORIES ──────────────────────────────────────── */}
          <div id="sec-document_cats" className="scroll-mt-[88px]">
            <Section title="Document Categories">
              {/* Tab selector */}
              <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl w-fit">
                {['client', 'staff'].map(t => (
                  <button key={t} onClick={() => setDocTab(t)}
                    aria-pressed={docTab === t}
                    className={clsx('px-4 py-1.5 text-xs font-medium rounded-lg transition-colors capitalize',
                      docTab === t ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700')}>
                    {t}
                  </button>
                ))}
              </div>

              <AddTagInput
                value={newDocCat}
                onChange={setNewDocCat}
                onAdd={addDocCat}
                placeholder={`New ${docTab} document category…`}
              />

              <div className="flex flex-wrap gap-2">
                {filteredDocCats.map(cat => (
                  <span key={cat.id}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-slate-50 py-1 pl-3 pr-1.5 text-sm font-medium text-slate-700 ring-1 ring-inset ring-slate-200/80">
                    {cat.name}
                    <button onClick={() => deleteDocCat(cat.id)} aria-label={`Remove ${cat.name}`}
                      className="rounded p-0.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600">
                      <X size={12} />
                    </button>
                  </span>
                ))}
                {filteredDocCats.length === 0 && <p className="text-sm text-slate-400">No {docTab} document categories yet.</p>}
              </div>
            </Section>
          </div>

          {/* ── NOTE TYPES ───────────────────────────────────────────────── */}
          <div id="sec-note_types" className="scroll-mt-[88px]">
            <Section title="Note Types">
              {/* The table scrolls sideways in its own box; `overflow-hidden` here used to clip it. */}
              <div className="rounded-xl border border-slate-100">
                <div className="overflow-x-auto rounded-xl">
                  <table className="table-base min-w-[480px]">
                    <thead>
                      <tr>
                        <th className="text-left">Name</th>
                        <th className="text-center">Mobile enabled</th>
                        <th className="text-center">Mandatory</th>
                        <th className="text-center">Active</th>
                      </tr>
                    </thead>
                    <tbody>
                      {noteTypes.map(nt => (
                        <tr key={nt.id}>
                          <td className="font-medium text-sm">{nt.name}</td>
                          <td className="text-center">
                            <Toggle checked={nt.mobile_enabled} label={`${nt.name}: mobile enabled`}
                              onChange={v => updateNoteType(nt.id, 'mobile_enabled', v)} />
                          </td>
                          <td className="text-center">
                            <Toggle checked={nt.is_mandatory} label={`${nt.name}: mandatory`}
                              onChange={v => updateNoteType(nt.id, 'is_mandatory', v)} />
                          </td>
                          <td className="text-center">
                            <Toggle checked={nt.is_active} label={`${nt.name}: active`}
                              onChange={v => updateNoteType(nt.id, 'is_active', v)} />
                          </td>
                        </tr>
                      ))}
                      {noteTypes.length === 0 && (
                        <tr><td colSpan={4} className="text-center py-6 text-slate-500">No note types</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </Section>
          </div>

          {/* ── PUBLIC HOLIDAYS ──────────────────────────────────────────── */}
          <div id="sec-public_holidays" className="scroll-mt-[88px]">
            <Section title="Public Holidays">
              {/* Default holidays */}
              <div>
                <GroupHeading>Default holidays</GroupHeading>
                <div className="space-y-1.5">
                  {publicHolidays.filter(h => !h.is_custom).map(h => (
                    <div key={h.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-50">
                      <span className="text-sm text-slate-700">{h.name}</span>
                      <span className="text-xs tabular-nums text-slate-500">
                        {new Date(h.date + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Custom holidays */}
              <div>
                <div className="mb-3 flex items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold text-slate-900">Custom holidays</h3>
                  <button onClick={() => setShowAddHoliday(v => !v)} className="btn-primary btn-sm gap-1">
                    <Plus size={12} /> Add Holiday
                  </button>
                </div>

                {showAddHoliday && (
                  <div className="flex flex-wrap gap-3 mb-4 p-3 bg-slate-50 rounded-xl border border-slate-200">
                    <input className="input-field flex-1 !py-2 text-sm" placeholder="Holiday name"
                      value={newHoliday.name} onChange={e => setNewHoliday(h => ({ ...h, name: e.target.value }))} />
                    <input type="date" className="input-field !w-auto !py-2 text-sm"
                      value={newHoliday.date} onChange={e => setNewHoliday(h => ({ ...h, date: e.target.value }))} />
                    <button onClick={addHoliday} className="btn-primary btn-sm">Save</button>
                    <button onClick={() => setShowAddHoliday(false)} className="btn-secondary btn-sm">Cancel</button>
                  </div>
                )}

                <div className="space-y-1.5">
                  {publicHolidays.filter(h => h.is_custom).map(h => (
                    <div key={h.id} className="flex items-center justify-between rounded-lg bg-primary-50/60 px-3 py-2">
                      <span className="text-sm text-slate-700">{h.name}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-xs tabular-nums text-slate-500">
                          {new Date(h.date + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                        <button onClick={() => deleteHoliday(h.id)} aria-label={`Delete ${h.name}`} className="rounded p-1 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  ))}
                  {publicHolidays.filter(h => h.is_custom).length === 0 && (
                    <p className="text-sm text-slate-400 py-2">No custom holidays added.</p>
                  )}
                </div>
              </div>
            </Section>
          </div>

          {/* ── PRICE BOOKS ──────────────────────────────────────────────── */}
          <div id="sec-price_books" className="scroll-mt-[88px]">
            <Section title="Price Books">
              <PriceBooks />
            </Section>
          </div>

          {/* ── FINANCIAL CONFIG ────────────────────────────────────────── */}
          <div id="sec-financial_config" className="scroll-mt-[88px]">
            <Section title="Financial Configuration">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { icon: DollarSign, title: 'Settings & Multipliers', desc: 'Fuel rate, shift start date, target margin, and public holiday / weekend charge & pay multipliers' },
                  { icon: Globe,      title: 'Public Holidays',         desc: 'Manage state-specific public holiday dates used for automatic billing uplift' },
                  { icon: Users,      title: 'Owners & Stakeholders',   desc: 'Configure revenue split percentages across business owners and stakeholders' },
                  { icon: Shield,     title: 'Superannuation Rates',    desc: 'Track historical and current SG rates (11.5%) for payroll compliance' },
                  { icon: Users,      title: 'Client Rates',             desc: 'NDIS charge rates per client and shift type with date-range validity' },
                  { icon: DollarSign, title: 'Staff Rates',              desc: 'Pay rates per staff member and shift type with date-range validity' },
                ].map(({ icon: Icon, title, desc }) => (
                  <div key={title} className="flex items-start gap-3 rounded-xl border border-slate-200/80 p-4">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-50 text-slate-500 ring-1 ring-inset ring-slate-200/80">
                      <Icon size={15} strokeWidth={1.9} />
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{title}</p>
                      <p className="mt-0.5 text-xs leading-relaxed text-slate-500">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-end">
                <Link to="/financials/config" className="btn-primary">
                  Open Financial Configuration <ChevronRight size={15} />
                </Link>
              </div>
            </Section>
          </div>

          {/* ── INTEGRATIONS ─────────────────────────────────────────────── */}
          <div id="sec-integrations" className="scroll-mt-[88px]">
            <Section title="Integrations">
              {/* NDIS Integrations */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-900">NDIS / Government</h3>
                <NDIAIntegration />
              </div>

              {/* Accounting Integrations */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-900">Accounting</h3>
                <XeroIntegration />
                <ContractorBillSettings />
                <QuickBooksIntegration />
              </div>

              {/* Other Integrations — platform-managed; an org sees the ones granted to it */}
              {otherIntegrations.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-slate-900">{isSuper ? 'Other integrations' : 'Payroll'}</h3>
                  <div className="space-y-2">
                    {otherIntegrations.map(integ => (
                      <OtherIntegrationCard key={integ.key} integration={integ} />
                    ))}
                  </div>
                </div>
              )}
            </Section>
          </div>

          {/* ── NOTIFICATIONS ────────────────────────────────────────────── */}
          <div id="sec-notifications" className="scroll-mt-[88px]">
            <Section title="Notifications">
              <NotificationsSection />
            </Section>
          </div>

          {/* ── SEND A NOTIFICATION (manual push to staff phones) ────────── */}
          <div id="sec-broadcast" className="scroll-mt-[88px]">
            <Section title="Send a notification to staff">
              <StaffBroadcast />
            </Section>
          </div>

          {/* ── SECURITY & MFA ───────────────────────────────────────────── */}
          <div id="sec-security" className="scroll-mt-[88px]">
            <Section title="Security & Two-Factor Authentication">
              <TwoFactorSettings />
            </Section>
          </div>

          {/* ── Unsaved-changes bar ────────────────────────────────────────
              Sticky, not fixed: it rides the bottom of the viewport while this column scrolls past,
              and at the end of the page it settles in the flow below the last card, so it never
              covers the last field. On phones it sits above the bottom menu (57px + the
              home-indicator inset). Save and Discard act on the section it names. */}
          {dirtyIds.length > 0 && (() => {
            const id = dirtyIds[0];
            const nameOf = (sid) => NAV_SECTIONS.find(s => s.id === sid)?.label || sid;
            const others = dirtyIds.slice(1).map(nameOf);
            return (
              <div ref={saveBarRef} role="region" aria-label="Unsaved changes"
                className="sticky bottom-[calc(57px+env(safe-area-inset-bottom,0px))] z-20 pb-3 md:bottom-0">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-[0_8px_24px_rgba(16,24,40,.12),0_1px_2px_rgba(16,24,40,.06)]">
                  <p className="flex min-w-0 flex-1 items-center gap-2 text-sm text-slate-700">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" aria-hidden="true" />
                    <span className="min-w-0">
                      Unsaved changes in{' '}
                      <button type="button" onClick={() => scrollTo(id)} title={`Go to ${nameOf(id)}`}
                        className="rounded font-semibold text-slate-900 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500">
                        {nameOf(id)}
                      </button>
                      {others.length > 0 && (
                        <span className="text-slate-500" title={`Also unsaved: ${others.join(', ')}`}>
                          {' '}· +{others.length} more<span className="sr-only">: {others.join(', ')}</span>
                        </span>
                      )}
                    </span>
                  </p>
                  <div className="ml-auto flex shrink-0 items-center gap-2">
                    <button type="button" onClick={() => discardSection(id)} disabled={saving[id]} className="btn-secondary btn-sm">
                      Discard
                    </button>
                    <button type="button" onClick={sectionSave[id]} disabled={saving[id]} className="btn-primary btn-sm">
                      {saving[id] ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                      {saving[id] ? 'Saving…' : 'Save changes'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </SettingsPage>
  );
}
